/* eslint-disable eqeqeq */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Phase 6 Vendor-Port W5 — COIL selector data layer (§33.2).
 *
 * The pure-data half of the coil selector: the ~40 catalog collection URL
 * builders (mfg/type/duty cascade filters), the casing_mtl_defaults map (copied
 * VERBATIM from coil.selector.js:187), the type/duty default maps, the
 * dependency-graph spec for useDependencyGraph, the type→coil_type derivation,
 * the field-visibility rules per coil_type, and the FOUR-WAY selectCoil write
 * payload builders (pure, so the verifier can read the exact write shapes).
 *
 * Legacy source: django/static/mvc/component_selector/components/coil/
 *   view/coil.selector.js (4,268) + collection/*.js (the urlRoot/url patterns).
 *
 * ★ DATA-SAFETY: this module is PURE (no fetch, no mutation). Every write shape
 * is a pure function the selector calls only inside a userArmed Select.
 */

import type { DependencyGraphSpec } from './useDependencyGraph';

// ---------------------------------------------------------------------------
// constants copied VERBATIM from coil.selector.js
// ---------------------------------------------------------------------------

/** casing_mtl_defaults: `${mfg}_${type}_${duty}` → casing_mtl id
 *  (coil.selector.js:187-214 — copy verbatim). */
export const CASING_MTL_DEFAULTS: Record<string, number> = {
  '79_1_1': 2666,
  '79_1_2': 264,
  '79_2_1': 2666,
  '79_3_2': 264,
  '79_4_4': 2666,
  '79_4_5': 2666,
  '79_4_3': 2666,

  '27_1_1': 2550,
  '27_1_2': 274,
  '27_2_1': 2550,
  '27_3_2': 274,
  '27_4_2': 274,

  '143_1_1': 2550,
  '143_1_2': 274,
  '143_2_1': 2550,
  '143_3_2': 274,
  '143_4_4': 2550,
  '143_4_5': 2550,
  '143_4_3': 2550,

  '181_1_1': 2550,
  '181_1_2': 274,
  '181_2_1': 2550,
  '181_3_2': 274,
};

/** Query fields for the "Available Coils: <n>" count (coil.selector.js:2949-
 * 3024 `filterDataCount`, GET `/api/v1.0/cms/1/v1.0.0/ahu/coils/all/count/`).
 * `filterDataCount`'s query-string builder ONLY reads `<select class=
 * "search_field">` elements (js:2959-2994 `if (elem.prop('tagName') ==
 * 'SELECT')` — the sibling `else if (... 'INPUT')` branch at :2995-3013 feeds
 * `self.rate_data` only, never `self.str`), so this list is: every SELECT in
 * coil.selector.tmpl carrying `search_field` MINUS legacy's explicit skip list
 * (js:2970-2974: duty_id / htfluid_id / feeds_id / air_stream_id /
 * outlet_header_dia / circuiting_id / inlet_conn_dia_id / conn_pipe_end_id /
 * connection_per_header / fpi_max_id / vent_drain_id / tube_cleanable_id /
 * pricing_method_id / style_id — several of these ARE backend-filterable but
 * legacy deliberately excludes them from the count query, so sending them
 * would UNDER-count relative to dev).
 * ★ `conn_protrusion_from_header` and `mounting_holes` are NOT here despite
 * being valid CMSCoilFilter fields — both are `<input>` elements in dev
 * (tmpl:79 numeric box, tmpl:1466 checkbox), not selects, so legacy's count
 * query never carries them. Verified live against the local MasterAHUCoil
 * catalog: sending `conn_protrusion_from_header=7` (the generic
 * `coilDefaults` seed) against SGS rows — which are ALL `9` — zeroed the
 * count outright; every other single field left the count unchanged. */
export const COIL_COUNT_FIELDS: string[] = [
  'mfg_id', 'type_id', 'rows_id', 'fpi_id', 'tube_mtl_id', 'tube_dia_id',
  'tube_wall_thick_id', 'tube_interior_id', 'fin_mtl_id', 'fh', 'fl',
  'fin_thick_id', 'fin_pattern_id', 'fin_finish_id', 'outlet_conn_dia_id',
  'conn_end_id', 'conn_hand_id', 'casing_mtl_id', 'conn_mtl_id',
  'casing_type_id',
  'casing_flange_header_side_id', 'casing_flange_return_side_id',
  'casing_flange_top_id', 'casing_flange_btm_id', 'evaporator_mode_id',
  'tube_vert_spacing_id', 'tube_hor_spacing_id',
];

/** RATING_AVAILABLE: mfg_id → rating route key (coil.selector.js:150). The mfg's
 * external_rating flag actually comes from coil_cms.coil.rating_mfg per-row; this
 * map is the legacy route lookup (informational — the external flag wins). */
export const COIL_RATING_AVAILABLE: Record<number, string> = {
  21: 'rating.coil.coilmaster',
  27: 'rating.coil.rae',
  79: 'rating.coil.modine',
  181: 'rating.coil.direct_coil',
  190: 'rating.coil.sgs_refrigeration',
};

/** defaults_depends_on_type (coil.selector.js:215). keyed `${type}_${duty}`. */
export const DEFAULTS_DEPENDS_ON_TYPE: Record<string, Record<string, number>> = {
  '1_1': { entering_fluid_temp: 45.0, desired_leaving_fluid_temp: 55.0 },
  '1_2': { entering_fluid_temp: 180.0, desired_leaving_fluid_temp: 140.0 },
  '2_1': { suction_temp: 45, ref_liquid_temp: 110 },
  '3_2': {},
  '4_3': { ref_liquid_temp: 0, entering_fluid_temp: 45.0, desired_leaving_fluid_temp: 65.0 },
  '4_4': { ref_liquid_temp: 0, entering_fluid_temp: 45.0, desired_leaving_fluid_temp: 65.0 },
  '4_5': { ref_liquid_temp: 0, entering_fluid_temp: 45.0, desired_leaving_fluid_temp: 65.0 },
};

/** defaults_depends_on_duty (coil.selector.js:246). keyed `${type}_${duty}`. */
export const DEFAULTS_DEPENDS_ON_DUTY: Record<string, Record<string, number>> = {
  '1_1': {
    eadb_temp: 80.0, eawb_temp: 67.0, desired_ladb_temp: 55.0, desired_lawb_temp: 54.0,
    entering_fluid_temp: 45.0, desired_leaving_fluid_temp: 55.0,
  },
  '1_2': {
    eadb_temp: 45.0, desired_ladb_temp: 85.0,
    entering_fluid_temp: 180.0, desired_leaving_fluid_temp: 140.0,
  },
  '2_1': {
    eadb_temp: 80.0, eawb_temp: 67.0, desired_ladb_temp: 55.0, desired_lawb_temp: 54.0,
    suction_temp: 45, ref_liquid_temp: 110,
  },
  '3_2': { eadb_temp: 45.0, desired_ladb_temp: 85.0 },
  '4_3': {
    ref_liquid_temp: 0, eadb_temp: 80.0, desired_ladb_temp: 55.0,
    entering_fluid_temp: 45.0, desired_leaving_fluid_temp: 65.0,
    vapor_temp: 200, condensing_temp: 110, subcooling_temp: 10, percent_condensing_load: 0,
  },
  '4_4': {
    ref_liquid_temp: 150, eadb_temp: 80.0, desired_ladb_temp: 55.0,
    entering_fluid_temp: 45.0, desired_leaving_fluid_temp: 65.0,
    vapor_temp: 200, condensing_temp: 110, subcooling_temp: 10, percent_condensing_load: 0,
  },
  '4_5': {
    ref_liquid_temp: 0, eadb_temp: 80.0, desired_ladb_temp: 55.0,
    entering_fluid_temp: 45.0, desired_leaving_fluid_temp: 65.0,
    vapor_temp: 200, condensing_temp: 110, subcooling_temp: 10, percent_condensing_load: 0,
  },
};

// ---------------------------------------------------------------------------
// coil_type derivation (getCoilType — coil.selector.js:1931). type_id → the
// type row's `value` string ('fluid' | 'evaporator' | 'condenser' | 'steam').
// The TYPE collection rows carry {item:{id, name, value}}, value being the
// machine string. The selector reads it off the loaded types list; we expose a
// helper that resolves from a types option list (preferred) with a static id
// fallback matching the canonical dev3 mapping.
// ---------------------------------------------------------------------------

// ksdm_master_ahu_coil_type on dev/dev3 is 1 Fluid, 2 Evaporator, 3 Steam,
// 4 Condenser. 3/4 were transposed here, so on any render that landed before
// the types list resolved every coil_type gate read 'condenser' for a steam
// coil and 'steam' for a condenser one.
export const COIL_TYPE_BY_ID_FALLBACK: Record<number, string> = {
  1: 'fluid',
  2: 'evaporator',
  3: 'steam',
  4: 'condenser',
};

export function coilTypeFromTypes(
  types: Array<{ item?: { id?: unknown; value?: unknown } }> | undefined,
  typeId: number | string | null | undefined
): string {
  const id = Number(typeId);
  const row = (types ?? []).find((t) => Number(t.item?.id) === id);
  if (row?.item?.value != null) return String(row.item.value).toLowerCase();
  return COIL_TYPE_BY_ID_FALLBACK[id] ?? '';
}

// ---------------------------------------------------------------------------
// collection URL builders (from the collection/*.js urlRoot+url patterns)
// each catalog is filtered by some subset of {mfg, type, duty, tube_dia,
// tube_mtl, fin_mtl}. Returns null when a required filter is missing.
// ---------------------------------------------------------------------------

const M = '/api/v1.0/master/v1.0.0/ahu/coils';

export interface CoilParents {
  mfg_id?: number | string | null;
  type_id?: number | string | null;
  duty_id?: number | string | null;
  tube_dia_id?: number | string | null;
  tube_mtl_id?: number | string | null;
  fin_mtl_id?: number | string | null;
  fin_pattern_id?: number | string | null;
  // Selector-parity (COIL): piping_ends is filtered by conn_mtl_id
  // (collection/piping_ends.js:12 — mfgs/<m>/types/<t>/mtls/<conn_mtl_id>/).
  conn_mtl_id?: number | string | null;
}

const need = (...v: Array<unknown>) => v.every((x) => x != null && x !== '' && x !== 'None');

/** URL for a coil catalog field, filtered by the current parents. null = skip. */
export function coilCollectionUrl(field: string, p: CoilParents): string | null {
  const { mfg_id, type_id, duty_id, tube_dia_id, tube_mtl_id, fin_mtl_id, conn_mtl_id } = p;
  switch (field) {
    // ---- Selector-parity (COIL) — GLOBAL routes (mfg-independent) ----
    // Manifold: conn End (collection/connection_ends.js:8 — flat urlRoot, no filter).
    case 'conn_end_id':
      return `${M}/connection_ends/`;
    // Coil tab: Hand (collection/hands.js:8 — /ahu/hands/, flat/global).
    case 'conn_hand_id':
      return '/api/v1.0/master/v1.0.0/ahu/hands/';
    // Pricing tab: Method (collection/pricing_methods.js:8 — /ahu/pricing_methods/, global).
    case 'pricing_method_id':
      return '/api/v1.0/master/v1.0.0/ahu/pricing_methods/';
    // ---- Selector-parity (COIL) — mfg + type routes ----
    // Manifold: conn Rotation (collection/connection_rotation.js:9 — /conn_rotations/).
    case 'conn_rotation_id':
      return need(mfg_id, type_id) ? `${M}/conn_rotations/mfgs/${mfg_id}/types/${type_id}/` : null;
    // Casing: Fastening method (collection/fastening_methods.js:8).
    case 'fastening_method_id':
      return need(mfg_id, type_id) ? `${M}/fastening_methods/mfgs/${mfg_id}/types/${type_id}/` : null;
    // Manifold: conn Pipe End — mfg + type + conn_mtl (collection/piping_ends.js:12).
    case 'conn_pipe_end_id':
      return need(mfg_id, type_id, conn_mtl_id)
        ? `${M}/piping_ends/mfgs/${mfg_id}/types/${type_id}/mtls/${conn_mtl_id}/` : null;
    // mfg-only
    case 'type_id':
      return need(mfg_id) ? `${M}/types/mfgs/${mfg_id}/` : null;
    // mfg + type
    case 'rows_id':
      return need(mfg_id, type_id) ? `${M}/rows/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'fpi_id':
      return need(mfg_id, type_id) ? `${M}/fpis/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'circuiting_id':
      return need(mfg_id, type_id) ? `${M}/circuitings/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'duty_id':
      return need(mfg_id, type_id) ? `${M}/duties/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'casing_flange_widths':
      return need(mfg_id, type_id) ? `${M}/casing_flange_widths/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'fin_finish_id':
      return need(mfg_id, type_id) ? `${M}/fin_finishs/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'connection_diameters':
      return need(mfg_id, type_id) ? `${M}/connection_diameters/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'fastening_methods':
      return need(mfg_id, type_id) ? `${M}/fastening_methods/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'condensate_conn_sizes':
      return need(mfg_id, type_id) ? `${M}/condensate_conn_sizes/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'casing_type_id':
      return need(mfg_id, type_id) ? `${M}/casing_types/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'fl':
      return need(mfg_id, type_id) ? `${M}/fls/mfgs/${mfg_id}/types/${type_id}/` : null;
    // fh is NOT the fls twin — its route adds a tube_dias segment
    // (urls_coil.py:126-129, legacy collection/fhs.js:8), so it needs
    // tube_dia_id too and belongs with the tube_dia-dependent lists.
    case 'fh':
      return need(mfg_id, type_id, tube_dia_id)
        ? `${M}/fhs/mfgs/${mfg_id}/types/${type_id}/tube_dias/${tube_dia_id}/` : null;
    case 'outlet_header_dia':
      return need(mfg_id, type_id) ? `${M}/outlet_header_dias/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'inlet_header_dia':
      return need(mfg_id, type_id) ? `${M}/inlet_header_dias/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'vent_drain_id':
      return need(mfg_id, type_id) ? `${M}/vent_drains/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'style_id':
      return need(mfg_id, type_id) ? `${M}/styles/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'header_mtl_id':
      return need(mfg_id, type_id) ? `${M}/header_mtls/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'conn_mtl_id':
      return need(mfg_id, type_id) ? `${M}/conn_mtls/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'casing_mtl_id':
      return need(mfg_id, type_id) ? `${M}/casing_mtls/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'tube_mtl_id':
      return need(mfg_id, type_id) ? `${M}/tube_mtls/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'fin_mtl_id':
      return need(mfg_id, type_id) ? `${M}/fin_mtls/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'feeds_id':
      return need(mfg_id, type_id) ? `${M}/feeds/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'casing_stacking_flange':
      return need(mfg_id, type_id) ? `${M}/casing_stacking_flanges/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'mounting_holes':
      return need(mfg_id, type_id) ? `${M}/mounting_holes/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'evaporator_mode_id':
      return need(mfg_id, type_id) ? `${M}/evaporator_modes/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'connection_rotation':
      // (legacy collection endpoint is /conn_rotations/ — kept for compat; the
      // live field key is 'conn_rotation_id' above.)
      return need(mfg_id, type_id) ? `${M}/conn_rotations/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'tube_cleanable_id':
      // collection/tube_cleanable.js:9 — /tube_cleanables/ (plural).
      return need(mfg_id, type_id) ? `${M}/tube_cleanables/mfgs/${mfg_id}/types/${type_id}/` : null;
    // mfg + type + tube_dia
    case 'tube_dia_id':
      return need(mfg_id, type_id) ? `${M}/tube_dias/mfgs/${mfg_id}/types/${type_id}/` : null;
    case 'tube_interior_id':
      return need(mfg_id, type_id, tube_dia_id)
        ? `${M}/tube_interiors/mfgs/${mfg_id}/types/${type_id}/tube_dias/${tube_dia_id}/` : null;
    case 'tube_hor_spacing_id':
      return need(mfg_id, type_id, tube_dia_id)
        ? `${M}/hor_spacings/mfgs/${mfg_id}/types/${type_id}/tube_dias/${tube_dia_id}/` : null;
    case 'tube_vert_spacing_id':
      return need(mfg_id, type_id, tube_dia_id)
        ? `${M}/vert_spacings/mfgs/${mfg_id}/types/${type_id}/tube_dias/${tube_dia_id}/` : null;
    // mfg + type + tube_dia + tube_mtl
    case 'tube_wall_thick_id':
      return need(mfg_id, type_id, tube_dia_id, tube_mtl_id)
        ? `${M}/tube_wall_thicks/mfgs/${mfg_id}/types/${type_id}/tube_dias/${tube_dia_id}/tube_mtls/${tube_mtl_id}/` : null;
    // mfg + type + tube_dia + fin_mtl
    case 'fin_pattern_id':
      return need(mfg_id, type_id, tube_dia_id, fin_mtl_id)
        ? `${M}/fin_patterns/mfgs/${mfg_id}/types/${type_id}/tube_dias/${tube_dia_id}/mtls/${fin_mtl_id}/` : null;
    case 'fin_thick_id':
      return need(mfg_id, type_id, tube_dia_id, fin_mtl_id)
        ? `${M}/fin_thicks/mfgs/${mfg_id}/types/${type_id}/tube_dias/${tube_dia_id}/fin_mtls/${fin_mtl_id}/` : null;
    // NO 'fin_id' case: legacy requires FinIdsCollection (coil.selector.js:60,
    // 134) but NEVER instantiates it — the fin_ids endpoint is never called by
    // the legacy selector. (For the record, the legacy collection URL shape is
    // fin_ids/tube_dias/<d>/fin_patterns/<p>/tube_vert_spacings/<v>/
    // tube_hor_spacings/<h> with NO trailing slash — fin_ids.js:8-12 /
    // urls_coil.py:186 — and the spacing params are BASE-table ids: the
    // MasterAHUCoilFinID FKs target MasterAHUCoilTube{Vert,Hor}Spacing,
    // models_coil.py:1105-1112.) The former 2-param trailing-slash build here
    // 404'd on every dialog open.
    // fluids (separate route: /ahu/fluids/types/<t>/duties/<d>/?mfg_id=)
    case 'htfluid_id':
      return need(type_id, duty_id)
        ? `/api/v1.0/master/v1.0.0/ahu/fluids/types/${type_id}/duties/${duty_id}/?mfg_id=${mfg_id ?? ''}` : null;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// the dependency graph (the cascade). mfg → type → {everything mfg+type};
// tube_dia → {interior, spacings, wall_thick, fin_pattern, fin_thick};
// tube_mtl → wall_thick; fin_mtl → fin_pattern/fin_thick; duty → fluids.
// ---------------------------------------------------------------------------

export const COIL_DEPENDENCY_GRAPH: DependencyGraphSpec = {
  mfg_id: {
    dependencies: ['type_id'],
  },
  type_id: {
    dependencies: [
      'rows_id', 'fpi_id', 'circuiting_id', 'duty_id', 'fin_finish_id', 'style_id',
      'header_mtl_id', 'conn_mtl_id', 'casing_mtl_id', 'tube_mtl_id', 'fin_mtl_id',
      'feeds_id', 'evaporator_mode_id', 'casing_type_id', 'vent_drain_id', 'fl', 'fh',
      'outlet_header_dia', 'inlet_header_dia', 'condensate_conn_sizes', 'tube_dia_id',
      // Selector-parity (COIL) — the restored Manifold/Casing tab lists. Shared
      // lists ('connection_diameters' → the 4 header/conn dia selects;
      // 'casing_flange_widths' → the 4 flange selects) load once per mfg/type,
      // like the existing 'condensate_conn_sizes' list key. The single-field
      // selects (conn_end/conn_rotation/fastening_method/tube_cleanable/conn_hand/
      // pricing_method) load their own lists; conn_end/conn_hand/pricing_method
      // are global routes (harmless reload on type change, keeps value in-list).
      'connection_diameters', 'casing_flange_widths', 'conn_end_id', 'conn_rotation_id',
      'fastening_method_id', 'tube_cleanable_id', 'conn_hand_id', 'pricing_method_id',
      // The fluids route is /ahu/fluids/types/<t>/duties/<d>/?mfg_id=<m> — keyed
      // by the MANUFACTURER as well as the type, and legacy re-fetches it inside
      // every getCollections pass (coil.selector.js:1214-1216, under the
      // tube_dia branch of the same cascade). With the edge only on duty_id a
      // manufacturer switch left the HT Fluid select showing the PREVIOUS
      // manufacturer's fluid list.
      'htfluid_id',
    ],
  },
  // Manifold: piping_ends is filtered by conn_mtl too (collection/piping_ends.js:12),
  // and legacy conn_mtl carries `collections_change_field` (tmpl:1121) → reload
  // conn_pipe_end_id when the connection material changes.
  conn_mtl_id: { dependencies: ['conn_pipe_end_id'] },
  duty_id: { dependencies: ['htfluid_id'] },
  tube_dia_id: {
    dependencies: [
      'tube_interior_id', 'tube_hor_spacing_id', 'tube_vert_spacing_id',
      'tube_wall_thick_id', 'fin_pattern_id', 'fin_thick_id',
      // the FH list is keyed by tube dia as well as mfg/type (see coilListUrl).
      'fh',
    ],
  },
  tube_mtl_id: { dependencies: ['tube_wall_thick_id'] },
  fin_mtl_id: { dependencies: ['fin_pattern_id', 'fin_thick_id'] },
  // NO fin_pattern_id→fin_id edge: legacy never fetches fin_ids (FinIdsCollection
  // is required at coil.selector.js:60,134 but never constructed) — the edge
  // fired a malformed fin_ids GET (404) on every dialog open / tube-dia change.
};

// ---------------------------------------------------------------------------
// the cascade RE-PICK policy (getCollections' per-field fallbacks + the
// template's implicit first-option fallback). See useDependencyGraph's
// `pickFallback`.
//
// Legacy has TWO layers and the port only had the first:
//
//  1. `getCollections` re-picks a handful of fields EXPLICITLY when the loaded
//     list no longer carries the current value — always to a concrete row, not
//     to null (coil.selector.js:911-915 type, :1049-1051 casing type,
//     :1100-1104 fl, :1116-1119 duty, :1145-1151 casing mtl, :1157-1160 conn
//     mtl, :1172-1175 tube mtl, :1207-1210 tube dia, :1275-1278 htfluid,
//     :1284-1287 fin mtl, :1299-1309 fh, :1334-1337 fin thick, :1338-1342 fin
//     pattern, :1252-1267 the tube spacings).
//  2. Everything else is re-picked by the BROWSER: the Handlebars template
//     marks `selected` only on the option whose id equals the stored value
//     (`{{#ifCompare item.id '==' ../params_coil.<field>}}`), so a value the
//     new list does not carry leaves NO option selected and the select falls
//     back to its FIRST option. On the selects that open with a `- -` option
//     that first option is the dash; on the ones that do not, it is the first
//     real row — and that displayed value is what legacy then rates and saves
//     (`filterDataCount` reads the DOM, coil.selector.js:2959-2965).
//
// The port implemented neither: the shared engine's built-in rule clears an
// out-of-range child to null unless the list has exactly one entry, so
// switching manufacturer emptied Fin Pattern, Fin Thickness, Casing Material,
// Casing Type, Tube Wall Thickness, Fin Finish… all of which legacy fills.
// ---------------------------------------------------------------------------

/** Fields whose legacy `<select>` opens with a `- -` / None option, so the
 * browser's implicit fallback IS the dash: an out-of-list value shows blank in
 * legacy too and must NOT be re-picked to the first row. Sourced from
 * coil.selector.tmpl (:203 rows, :222 fpi, :262/:265 circuiting, :276/:286
 * feeds, :998/:1008/:1036/:1056/:1081/:1099 header dias, :1141/:1161 conn dias,
 * :1213 pipe end, :1630 vent & drain, :1665 tube cleanable). fin_pattern_id is
 * NOT in the set: its dash exists only for mfg 27 (:427) and the manufacturer
 * switch re-picks it explicitly (js:1338-1342). casing_type_id is not either:
 * its dash carries `class="hide"` (:1390) and js:1049-1051 re-picks the first
 * row. */
export const COIL_NONE_OPTION_FIELDS = new Set<string>([
  'rows_id', 'fpi_id', 'circuiting_id', 'feeds_id',
  'outlet_header_dia', 'inlet_header_dia', 'outlet_conn_dia_id', 'inlet_conn_dia_id',
  'conn_pipe_end_id', 'vent_drain_id', 'tube_cleanable_id',
]);

export interface CoilFallbackCtx {
  /** the live selector state (mfg_id / type_id / duty_id / fh are read). */
  state: Record<string, unknown>;
  /** legacy `self.changeMFG` (js:2802/:2819) — the change that started this
   * cascade was a Manufacturer pick. Only the fin-pattern rule reads it. */
  mfgChanged: boolean;
}

/**
 * The coil re-pick policy — one function for both layers above.
 * `undefined` = defer to the engine's built-in rule.
 */
export function coilPickFallback(
  field: string,
  ids: Array<number | string>,
  current: number | string | null | undefined,
  ctx: CoilFallbackCtx
): number | string | null | undefined {
  if (!ids.length) return undefined;
  const has = (v: unknown) => ids.some((e) => String(e) == String(v));
  const first = ids[0];

  switch (field) {
    // Not fields: shared LISTS several selects read through `listKey`
    // (connection_diameters → the header/conn diameter selects,
    // casing_flange_widths → the four flange selects). They have no state value
    // of their own, so leave the engine's existing no-op alone.
    case 'connection_diameters':
    case 'casing_flange_widths':
    case 'condensate_conn_sizes':
      return undefined;

    // js:1203-1211 — the tube diameter falls to the SECOND row, not the first
    // (`self.collections.tube_dias[1].item.id`). On the live 3-row lists that
    // is 1/2", which is the value dev lands on.
    case 'tube_dia_id':
      return ids[1] ?? first;

    // js:1299-1310 — FH keeps the largest available height <= the previous one,
    // and only falls to the smallest when every option is taller.
    case 'fh': {
      const prev = Number(current);
      if (Number.isFinite(prev)) {
        const below = ids
          .map((v) => Number(v))
          .filter((v) => Number.isFinite(v) && v <= prev)
          .sort((a, b) => b - a);
        if (below.length) return below[0];
      }
      return first;
    }

    // js:1123-1152 — casing_mtl_defaults[`mfg_type_duty`] first, then the
    // current value if the new list still carries it, then the first row.
    case 'casing_mtl_id': {
      const key = `${ctx.state.mfg_id}_${ctx.state.type_id}_${ctx.state.duty_id}`;
      const mapped = CASING_MTL_DEFAULTS[key];
      if (mapped != null && has(mapped)) return mapped;
      if (current != null && has(current)) return current;
      return first;
    }

    // js:1338-1342 — a MANUFACTURER change pins the fin pattern to 3 (Waffle)
    // for CoilMaster/RAE and 2 for everyone else, falling to the first row when
    // the new list has neither. Outside a manufacturer change legacy leaves the
    // field alone (its select opens on `- -` for mfg 27, on the first pattern
    // otherwise) — defer, which lands on the same `- -`.
    case 'fin_pattern_id': {
      if (!ctx.mfgChanged) return undefined;
      const mfg = Number(ctx.state.mfg_id);
      const want = mfg === 21 || mfg === 27 ? 3 : 2;
      return has(want) ? want : first;
    }

    default:
      // Layer 2: no leading `- -` ⇒ the legacy select shows (and rates on) its
      // first real row. With one ⇒ the dash, i.e. the engine's null.
      return COIL_NONE_OPTION_FIELDS.has(field) ? undefined : first;
  }
}

/**
 * The Manufacturer-change side effects — `changeParams`'s `mfg_id` branch
 * (coil.selector.js:2781-2820) plus the shared mfg/type tail (:2846-2895),
 * which the port did not carry at all. Returns the patch to merge into state.
 *
 * `coilType` is the type string in force AFTER the change (mfg 190 forces
 * type 2 / evaporator first, js:2783-2787).
 */
export function coilMfgChangePatch(
  mfgId: number | string | null | undefined,
  prevState: Record<string, unknown>,
  coilTypeOf: (typeId: unknown) => string
): Record<string, unknown> {
  const mfg = Number(mfgId);
  const patch: Record<string, unknown> = {};

  // js:2783-2787 — SGS Refrigeration is evaporator-only and right-hand.
  let typeId: unknown = prevState.type_id;
  if (mfg === 190) {
    typeId = 2;
    patch.type_id = 2;
    patch.conn_hand_id = 1;
  }
  const coilType = coilTypeOf(typeId);

  // js:2782 `updateCondenserFields(null, true)` — the reset branch (js:4638).
  Object.assign(patch, coilCondenserFieldsPatch(typeId, true));
  // js:2812 — the previous manufacturer's air-pressure-drop result is stale.
  patch.apd = null;

  Object.assign(patch, coilMfgTypeTailPatch(mfg, typeId, coilType));
  return patch;
}

/**
 * `updateCondenserFields` (coil.selector.js:4636-4650). Condenser (type 4) SEEDS
 * the four condenser boxes plus the liquid ref temp; every other type — and the
 * explicit `reset` the manufacturer branch passes (js:2782) — clears the four.
 * Only the reset arm was ported, so picking Type = Condenser left those boxes on
 * the previous type's numbers instead of 120 / 95 / 10 / 80.
 */
export function coilCondenserFieldsPatch(
  typeId: unknown,
  reset = false
): Record<string, unknown> {
  if (Number(typeId) !== 4 || reset) {
    return {
      vapor_temp: null,
      condensing_temp: null,
      subcooling_temp: null,
      percent_condensing_load: null,
    };
  }
  return {
    vapor_temp: 120,
    condensing_temp: 95,
    subcooling_temp: 10,
    percent_condensing_load: 80,
    ref_liquid_temp: 110,
  };
}

/**
 * The tail of `changeParams` that legacy runs for BOTH a manufacturer and a type
 * pick (coil.selector.js:2846-2896) — the connection/header diameter reset and
 * the per-coil_type pins. Sequential `if`s, NOT else-ifs, so a later pin
 * overwrites an earlier one (mfg 79 + evaporator ends on htfluid 28, not 13).
 */
export function coilMfgTypeTailPatch(
  mfg: number,
  typeId: unknown,
  coilType: string
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};

  // js:2847-2855 — connection + header diameters reset, except the Super
  // Radiator steam pair which is pinned to 2-1/4".
  const isSrcSteam = mfg === 143 && Number(typeId) === 3;
  patch.outlet_conn_dia_id = isSrcSteam ? 16 : null;
  patch.inlet_conn_dia_id = isSrcSteam ? 16 : null;
  patch.outlet_header_dia = isSrcSteam ? '2.1250' : null;
  patch.inlet_header_dia = isSrcSteam ? '2.1250' : null;

  // js:2856-2894 — the per-type pins, in legacy's order (later wins).
  if (coilType === 'fluid') {
    patch.htfluid_id = 1;
    patch.feeds_id = null;
  }
  if (mfg !== 190 && coilType === 'evaporator') patch.htfluid_id = 13;
  if (mfg === 27 && coilType === 'evaporator') patch.style_id = 2;
  if (mfg === 79 && coilType === 'evaporator') {
    patch.style_id = 11;
    patch.feeds_id = 12;
  }
  if (coilType === 'condenser') {
    patch.style_id = 9;
    patch.feeds_id = 12;
  }
  if ((mfg === 143 || mfg === 79) && (coilType === 'condenser' || coilType === 'evaporator')) {
    patch.htfluid_id = 28;
  }
  return patch;
}

/**
 * The Type branch of `changeParams` (coil.selector.js:2821-2834) plus the shared
 * tail it falls through to (:2846-2896), in legacy's order: the type defaults,
 * then updateCondenserFields (which is why Condenser's 120/95/10/80 beat the
 * DEFAULTS_DEPENDS_ON_TYPE row), then the conn-diameter reset and the pins.
 *
 * Nothing called this before: the only `coilTypeDutyDefaultsPatch(true, …)` call
 * site was the CASCADE re-pick, so a user's own Type pick renarrowed the option
 * lists and changed nothing else.
 */
export function coilTypeChangePatch(
  typeId: unknown,
  prevState: Record<string, unknown>,
  coilTypeOf: (typeId: unknown) => string
): Record<string, unknown> {
  return {
    ...coilTypeDutyDefaultsPatch(true, typeId as number | string, prevState),
    ...coilCondenserFieldsPatch(typeId),
    ...coilMfgTypeTailPatch(Number(prevState.mfg_id), typeId, coilTypeOf(typeId)),
  };
}

/**
 * The Duty branch of `changeCoilField` (coil.selector.js:2518-2530): the duty
 * defaults, then the connection/header diameter reset. Note it does NOT run the
 * per-coil_type pins — those belong to the mfg/type tail only.
 */
export function coilDutyChangePatch(
  dutyId: unknown,
  prevState: Record<string, unknown>
): Record<string, unknown> {
  const isSrcSteam =
    Number(prevState.mfg_id) === 143 && Number(prevState.type_id) === 3;
  return {
    ...coilTypeDutyDefaultsPatch(false, dutyId as number | string, prevState),
    outlet_conn_dia_id: isSrcSteam ? 16 : null,
    inlet_conn_dia_id: isSrcSteam ? 16 : null,
    outlet_header_dia: isSrcSteam ? '2.1250' : null,
    inlet_header_dia: isSrcSteam ? '2.1250' : null,
  };
}

/**
 * `changeDefaultDependsOnTypeAndDuty` (coil.selector.js:2323-2372) — the
 * temperature defaults that follow a type / duty re-pick. Returns the patch.
 *
 * `changeIfNullOrUndefined` (js:2299-2303) reads as "only when null", but its
 * `|| data[name] !== undefined` arm means the table value ALWAYS wins when the
 * table has the key; a key the table omits is cleared only when the current
 * value is already null/undefined. Mirrored exactly.
 */
export function coilTypeDutyDefaultsPatch(
  isOnlyTypeChanged: boolean,
  value: number | string,
  prevState: Record<string, unknown>
): Record<string, unknown> {
  const typeId = isOnlyTypeChanged ? value : prevState.type_id;
  let dutyId: unknown = isOnlyTypeChanged ? prevState.duty_id : value;
  if (isOnlyTypeChanged) {
    if (Number(value) === 2) dutyId = 1;
    else if (Number(value) === 3) dutyId = 2;
  }
  let onlyType = isOnlyTypeChanged;
  if (String(dutyId) != String(prevState.duty_id)) onlyType = false;

  const patch: Record<string, unknown> = {};
  const assign = (name: string, data: Record<string, number>) => {
    const cur = prevState[name];
    if (cur === null || cur === undefined || data[name] !== undefined) {
      patch[name] = data[name] ?? null;
    }
  };

  if (onlyType) {
    const data = DEFAULTS_DEPENDS_ON_TYPE[`${value}_${dutyId}`];
    if (data) {
      assign('entering_fluid_temp', data);
      assign('desired_leaving_fluid_temp', data);
    }
  } else {
    const data = DEFAULTS_DEPENDS_ON_DUTY[`${typeId}_${dutyId}`];
    if (data) {
      for (const name of [
        'desired_ladb_temp', 'entering_fluid_temp', 'desired_leaving_fluid_temp',
        'desired_lawb_temp', 'suction_temp', 'ref_liquid_temp', 'vapor_temp',
        'condensing_temp', 'subcooling_temp', 'percent_condensing_load',
        'desired_capacity_total', 'desired_capacity_total_total',
      ]) assign(name, data);
      // js:2358-2359 — the section pair is assigned directly, table value only.
      if (data.eadb_temp !== undefined) patch.eadb_temp = data.eadb_temp;
      if (data.eawb_temp !== undefined) patch.eawb_temp = data.eawb_temp;
    }
  }

  // js:2365-2371 — steam/evaporator carry the superheat + steam pressure pair.
  if (Number(typeId) === 2 || Number(typeId) === 3) {
    patch.entering_superheat = prevState.entering_superheat ?? 8;
    patch.steam_pressure = prevState.steam_pressure ?? 5;
  } else {
    patch.entering_superheat = null;
    patch.steam_pressure = null;
  }
  return patch;
}

// ---------------------------------------------------------------------------
// option label-key map (verified against coil.selector.tmpl) — the rows are
// {item:{id, name, value, ...}} except mfg/htfluid/feeds(DC)/conn_hand which are
// FLAT {id, name, value}. valueKey = which row field is the <option value>,
// textKey = the <option> text. (the W4 labelKey lesson.)
// ---------------------------------------------------------------------------

export interface CoilOptKey {
  /** the option row is wrapped {item:{...}} (true) or flat {...} (false). */
  wrapped: boolean;
  /** the field used as the <option value> (always the id). */
  valueKey: 'id';
  /** the field used as the <option> text. */
  textKey: 'name' | 'value' | 'label';
  /** which item field is the OPTION/ENGINE id. Defaults to 'id'; legacy's FL
   * select is VALUE-keyed (option value={{item.value}} — coil.selector.tmpl:187),
   * so 'fl' resolves ids off item.value. */
  idKey?: 'id' | 'value';
}

export const COIL_OPTION_KEYS: Record<string, CoilOptKey> = {
  mfg_id: { wrapped: false, valueKey: 'id', textKey: 'name' },
  type_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  duty_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  htfluid_id: { wrapped: false, valueKey: 'id', textKey: 'name' },
  evaporator_mode_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  rows_id: { wrapped: true, valueKey: 'id', textKey: 'value' },
  fpi_id: { wrapped: true, valueKey: 'id', textKey: 'value' },
  style_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  circuiting_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  feeds_id: { wrapped: true, valueKey: 'id', textKey: 'value' },
  tube_dia_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  tube_wall_thick_id: { wrapped: true, valueKey: 'id', textKey: 'value' },
  tube_mtl_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  tube_interior_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  fin_mtl_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  fin_finish_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  fin_thick_id: { wrapped: true, valueKey: 'id', textKey: 'value' },
  fin_pattern_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  casing_mtl_id: { wrapped: true, valueKey: 'id', textKey: 'label' },
  casing_type_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  conn_hand_id: { wrapped: false, valueKey: 'id', textKey: 'name' },
  conn_mtl_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  header_mtl_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  vent_drain_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  // tube spacings: the /hor_spacings|/vert_spacings responses are JOIN rows —
  // {id:<MfgTypeDia JOIN id>, item:{<BASE ksdm_master_ahu_coil_tube_*_spacing
  // row>}} (rest_master/serializers.py:1307-1337). Legacy keys EVERYTHING on the
  // BASE item.id: option value={{item.id}} data-value={{item.value}}
  // (coil.selector.tmpl:1492-1501), params_coil.tube_*_spacing_id = item.id
  // (coil.selector.js:1063-1078) → the PATCH rating_mode_coils payload
  // (coil.selector.js:3371-3374). Without these entries coilOptionId fell back
  // to the flat row.id — the JOIN id — → IntegrityError on the select persist.
  tube_vert_spacing_id: { wrapped: true, valueKey: 'id', textKey: 'value' },
  tube_hor_spacing_id: { wrapped: true, valueKey: 'id', textKey: 'value' },
  // fl: legacy's FL select is VALUE-keyed — option value={{item.value}}, text
  // {{item.name}} (coil.selector.tmpl:185-190); the keep-check compares
  // item.value == params_coil.fl (coil.selector.js:904-909). idKey:'value'
  // keeps the seeded master_coil.fl (coil.selector.js:588) valid in the engine
  // instead of comparing it against JOIN row ids (which wiped it on open).
  fl: { wrapped: true, valueKey: 'id', idKey: 'value', textKey: 'name' },
  // fh is the twin of fl — same wrapped {item:{id,name,value}} shape and the
  // same VALUE-keyed option (coil.selector.tmpl:178-181), and the keep-check
  // compares item.value == params_coil.fh (coil.selector.js:904-909).
  fh: { wrapped: true, valueKey: 'id', idKey: 'value', textKey: 'name' },

  // ---- Selector-parity (COIL) — restored Manifold / Casing / Options / Pricing ----
  // Manifold: End (flat {id,name,value} — tmpl:1181-1182), Pipe End / Rotation
  // (wrapped {item:{id,name,value}} — tmpl:1216-1217/:1262-1264). conn_hand_id
  // already declared above (flat).
  conn_end_id: { wrapped: false, valueKey: 'id', textKey: 'name' },
  conn_pipe_end_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  conn_rotation_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  // Casing: Fastening method (wrapped, text=name — tmpl:1478-1479).
  fastening_method_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  // Options: Tube Cleanable (wrapped, text=name — tmpl:1666-1668).
  tube_cleanable_id: { wrapped: true, valueKey: 'id', textKey: 'name' },
  // Pricing: Method (flat {id,name,value} — pricing_methods, tmpl:1764-1766).
  pricing_method_id: { wrapped: false, valueKey: 'id', textKey: 'name' },
  // Options: Fin Spacing Maximum — reads the shared fpi_id list (wrapped fpi
  // rows), keyed by item.id, text=item.value (tmpl:1552-1554).
  fpi_max_id: { wrapped: true, valueKey: 'id', textKey: 'value' },
  // Manifold header diameters — VALUE-keyed (option value={{item.value}},
  // text={{item.value}} — tmpl:1038-1039/:1058-1059), off the connection_diameters
  // list. conn diameters are ID-keyed (value={{item.id}}, text={{item.value}},
  // data-value={{item.value}} — tmpl:1144/:1164).
  outlet_header_dia: { wrapped: true, valueKey: 'id', idKey: 'value', textKey: 'value' },
  inlet_header_dia: { wrapped: true, valueKey: 'id', idKey: 'value', textKey: 'value' },
  outlet_conn_dia_id: { wrapped: true, valueKey: 'id', textKey: 'value' },
  inlet_conn_dia_id: { wrapped: true, valueKey: 'id', textKey: 'value' },
  // Casing flanges — ID-keyed, text=width value, data-value=width value
  // (tmpl:1410-1411 etc.), off the casing_flange_widths list.
  casing_flange_header_side_id: { wrapped: true, valueKey: 'id', textKey: 'value' },
  casing_flange_return_side_id: { wrapped: true, valueKey: 'id', textKey: 'value' },
  casing_flange_top_id: { wrapped: true, valueKey: 'id', textKey: 'value' },
  casing_flange_btm_id: { wrapped: true, valueKey: 'id', textKey: 'value' },
};

/** Pull the DISPLAY label off an option row, honoring the wrapped/flat +
 * text-key map. Use this for the select text the user sees — NOT the rate
 * input (the rate input needs the vendor CODE; see coilOptionValue / BUG 3). */
export function coilOptionLabel(row: any, field: string): string {
  const key = COIL_OPTION_KEYS[field];
  const obj = key?.wrapped ? row.item ?? row : row;
  const tk = key?.textKey ?? 'name';
  return String(obj?.[tk] ?? obj?.name ?? obj?.value ?? '');
}

/** Pull the RATE-INPUT VENDOR CODE off a master option row (BUG 3). The vendor
 * keys on the row `.value` (tube_dia "0.625", Copper→"copper",
 * Aluminum→"aluminum", Waffle→"waffle"), NOT the human label "5/8"/"Copper".
 * Legacy coil.selector.js:2691 sends `option.data('value') ?? elem.val()`, where
 * data-value is the master row's `.value`. Falls to `.name` then the id. */
export function coilOptionValue(row: any, field: string): string | undefined {
  const key = COIL_OPTION_KEYS[field];
  const obj = key?.wrapped ? row.item ?? row : row;
  const v = obj?.value ?? obj?.name ?? obj?.id;
  return v == null ? undefined : String(v);
}

/** Pull the id off an option row (item.id when wrapped; item.value for the
 * VALUE-keyed fields — idKey:'value', e.g. fl). */
export function coilOptionId(row: any, field: string): number | string {
  const key = COIL_OPTION_KEYS[field];
  const obj = key?.wrapped ? row.item ?? row : row;
  if (key?.idKey === 'value') return obj?.value ?? obj?.id ?? row;
  return obj?.id ?? obj?.value ?? row;
}

// ---------------------------------------------------------------------------
// field visibility per coil_type — the per-type tmpl blocks.
// ---------------------------------------------------------------------------

// Legacy gates each block on ONE coil_type — `{{#ifCompare coil_type '==' X}}`
// — and the four blocks are mutually exclusive (tmpl:710 steam, :754 condenser,
// :841 evaporator, :908 fluid). `condenser` used to be `evaporator ||
// condenser`, which put Vapor / Condensing / Subcooling / Condensing Load on an
// evaporator and Saturated Suction Temp on a condenser; neither appears on dev.
export interface CoilTypeVisibility {
  fluidFlow: boolean;      // EFT / LFT / fluid flow / fouling      (tmpl:908)
  refrigerant: boolean;    // evaporator mode, SST + liquid ref,
                           // vapor velocity | FPD, superheat        (tmpl:841)
  condenser: boolean;      // vapor / liquid ref / condensing /
                           // subcooling / condensing load           (tmpl:754)
  steam: boolean;          // entering superheat / steam pressure    (tmpl:710)
}

export function coilTypeVisibility(coilType: string): CoilTypeVisibility {
  const t = coilType.toLowerCase();
  return {
    fluidFlow: t === 'fluid',
    refrigerant: t === 'evaporator',
    condenser: t === 'condenser',
    steam: t === 'steam',
  };
}

// ---------------------------------------------------------------------------
// the .pair_field disablePair pairs (glycol). data-pair="a__b" means: when one
// of the pair has a value, disable the others. (coil.selector.js:2605.)
// ---------------------------------------------------------------------------

export const COIL_PAIR_FIELDS: Record<string, string[]> = {
  // glycol_percent ↔ entering_fluid_temp style pairs are template-driven; the
  // canonical glycol pair on dev3 toggles glycol_percent vs fluid temps. The
  // exact data-pair strings live in the tmpl; we expose the helper + the known
  // glycol pair (fouling factors in/out are the documented pair).
  fouling_factor_inside: ['fouling_factor_outside'],
  fouling_factor_outside: ['fouling_factor_inside'],
};

// ---------------------------------------------------------------------------
// the two RATE inputs (§33.2). DB-route (manufacturer:'manual') reads a NUMERIC
// field set (self.rate_data); the VENDOR route reads the search-field DOM
// (self.data). Both ship as {manufacturer, part:'coil', type:coil_type, input}.
// We build inputs from the state model (the React analogue of reading the DOM).
// ---------------------------------------------------------------------------

/** field set that goes into the KSDM-DB ('manual') rate input as NUMBERS.
 *
 * ★ `fh` / `fl` were missing. Legacy's `filterDataCount` sweeps EVERY
 * `.search_field` select into `rate_data` except an explicit exclusion list
 * (`duty_id`/`htfluid_id`/`feeds_id`/`air_stream_id`/`outlet_header_dia`/
 * `circuiting_id`/`inlet_conn_dia_id`/`conn_pipe_end_id`/`connection_per_header`/
 * `fpi_max_id`/`vent_drain_id`/`tube_cleanable_id`/`pricing_method_id`/
 * `style_id`, coil.selector.js:2971-2975) — and the FH x FL pair
 * (coil.selector.tmpl:177-190) is not on it, so dev ships both.
 *
 * They are not decorative: `coil_selection` lists them in `filter_by_field_name`
 * (ahu/rating/coil_selection_rating_database.py:29-31) and filters
 * `MasterAHUCoil` on them directly. Without them the DB route searched the
 * WHOLE catalog regardless of finned height/length — on dev3 that is 1,805,440
 * selectable rows against the 260 that `fh=35, fl=48` narrows to — so the
 * "nearest FH" the dialog had just picked had no effect on the rated result.
 * Captured live on dev3 (SGS Refrigeration, the one manufacturer here whose CMS
 * `external_rating` is false): 14-key input, no `fh`, no `fl`. */
export const COIL_DB_INPUT_FIELDS = [
  'fh', 'fl',
  'rows', 'fpi', 'tube_dia', 'tube_wall_thick', 'tube_mtl', 'fin_mtl', 'fin_finish',
  'fin_thick', 'fin_pattern', 'casing_mtl', 'percent_condensing_load', 'subcooling_temp',
  'condensing_temp', 'vapor_temp', 'eadb_temp', 'eawb_temp', 'face_velocity_actual', 'apd',
  'fluid_pd', 'entering_superheat', 'ref_liquid_temp', 'suction_temp', 'capacity_sensible',
  'capacity_total', 'desired_capacity_total', 'desired_ladb_temp', 'desired_lawb_temp',
  'tube_vapor_velocity', 'leaving_superheat',
];

export interface CoilRateState extends Record<string, any> {}

/** Build the VENDOR-route input (self.data): the search-field DOM values keyed
 * by data-name; casing_mtl carries mtltype/gauge; spacings carry id+value. */
export function buildVendorRateInput(s: CoilRateState): Record<string, unknown> {
  const input: Record<string, unknown> = {};
  // the vendor input mirrors the search-field selection text/values; we send
  // the rated-coil params that drive the catalog search.
  for (const k of [
    'rows', 'fpi', 'circuiting', 'feeds', 'tube_dia', 'tube_wall_thick', 'tube_mtl',
    'fin_mtl', 'fin_finish', 'fin_thick', 'fin_pattern', 'fh', 'fl',
    // Selector-parity: glycol_percent is a fluid-coil `.search_field`
    // (coil.selector.tmpl:124-135); legacy's rate sweep ships it so the glycol
    // concentration reaches the vendor. Previously in the SELECT write only.
    'glycol_percent',
    // Selector-parity FIX: only the ALL-COILS box carries `search_field`
    // (airflow_actual_total, tmpl:527); the per-coil `airflow_actual` box is
    // `per_coil_field required_field` (tmpl:520) and never reaches the vendor.
    // React edits only the total, so sending the stale per-coil `airflow_actual`
    // dropped the user's edit → face velocity 0. Ship the total (matches the
    // header builder + coil.selector.js:3599 which saves airflow_actual_total).
    'eadb_temp', 'eawb_temp', 'face_velocity_actual', 'apd', 'airflow_actual_total',
    // ★ `desired_capacity_total_total` (tmpl:701-707) is an unconditional
    // `search_field` that dev's sweep ships on the board route too; only the
    // header builder had it. Super Radiator consumes it.
    'desired_capacity_total', 'desired_capacity_total_total',
    'desired_ladb_temp', 'desired_lawb_temp',
    'entering_fluid_temp', 'desired_leaving_fluid_temp', 'fluid_pd', 'suction_temp',
    'ref_liquid_temp', 'entering_superheat', 'tube_vapor_velocity', 'leaving_superheat',
    'subcooling_temp', 'condensing_temp', 'vapor_temp', 'percent_condensing_load',
    'tube_vert_spacing', 'tube_hor_spacing', 'htfluid', 'casing_mtl', 'casing_gauge',
    // 5e-sync (KA-5259): the backend's add_min_aw_dimensions_to_models
    // (network_control/rating/tools/tools.py:109-153) computes each candidate's
    // min_aw_width/min_aw_height from THESE rating-input keys — including the
    // flange widths. `duty` is the duty row's machine value ('cooling' —
    // resolved by the caller). casing_flange_top/btm are the flange-width VALUE
    // fields (coil.selector.tmpl:1444/:1461, data-name="casing_flange_top"/
    // "casing_flange_btm"); the Casing-tab Flange Top/Bottom selects now keep
    // these in lockstep with their _id (see the flange id↔value sync effect).
    'duty', 'qty_stacked', 'h_from_floor', 'mounting_angle',
    'casing_flange_top', 'casing_flange_btm',
  ]) {
    if (s[k] != null && s[k] !== '') input[k] = String(s[k]);
  }
  // Selector-parity: Air PD Maximum is a real Options-tab control
  // (coil.selector.tmpl:1569); ship the user's value, defaulting to the legacy
  // `rate()` fallback 3 (coil.selector.js:2880) only when it is blank.
  if (s.apd_max != null && s.apd_max !== '') input.apd_max = String(s.apd_max);
  if (!('apd_max' in input)) input.apd_max = 3;
  // Selector-parity: Fluid PD Maximum — fluid coils only (tmpl:1575-1589),
  // template default 20 (tmpl:1584). The caller passes coil_type on the state.
  if (coilTypeVisibility(String((s as any).coil_type ?? '')).fluidFlow) {
    input.fluid_pd_max =
      s.fluid_pd_max != null && s.fluid_pd_max !== '' ? String(s.fluid_pd_max) : '20';
    // ★ the fluid-flow pair is a fluid-only `search_field` pair (tmpl:945/:948)
    // that dev's sweep ships and this builder never emitted — so a coil sized
    // BY FLOW (LFT left blank) reached the vendor with neither a leaving fluid
    // temperature nor a flow. It is the EITHER/OR twin of
    // `desired_leaving_fluid_temp`: with an LFT set the template renders both
    // boxes EMPTY (`{{#unless model.desired_leaving_fluid_temp}}`), so the
    // sweep reads '' and drops them.
    if (s.desired_leaving_fluid_temp == null || s.desired_leaving_fluid_temp === '') {
      if (s.fluid_flow != null && s.fluid_flow !== '') input.fluid_flow = String(s.fluid_flow);
      if (s.fluid_flow_total != null && s.fluid_flow_total !== '')
        input.fluid_flow_total = String(s.fluid_flow_total);
    }
  }
  return input;
}

// ---------------------------------------------------------------------------
// 5e-sync (KA-5259) — the client-side REQUIRED AW HEIGHT recompute
// (coil.selector.js updateRequiredAwHeights, :2337-2380 on merged dev).
// Runs at the top of every render_outputs so a stale/cached rated set shows
// the height for the coil's CURRENT h_from_floor / mounting_angle (those are
// `without_rate` fields — changing them re-renders outputs WITHOUT a re-rate).
// min_aw_width stays the backend value; only min_aw_height is recomputed.
// ---------------------------------------------------------------------------

/** the two CMS mfg-settings fields the recompute needs
 * (cms/models_coil.py CMSAHUCoilMfgSettings — min_clear_top /
 * coil_to_coil_vertical_clear; fetched off
 * /cms/1/v1.0.0/ahu/coils/mfg/settings/<duty>s/). */
export interface CoilCmsSettingsAw {
  min_clear_top?: number | string | null;
  coil_to_coil_vertical_clear?: number | string | null;
}

/**
 * Pure mirror of updateRequiredAwHeights. Returns a NEW row array with
 * min_aw_height recomputed in place of the response value (legacy mutates
 * self.rated_coils[*]); rows are returned UNCHANGED when any required input
 * is missing (the legacy early returns).
 *
 * Deviation (documented): legacy resolves casing_flange_top/btm by id against
 * collections.casing_flange_widths (matching the <select> option values); the
 * React form has no flange selects, so the serializer VALUE fields
 * (s.casing_flange_top / s.casing_flange_btm — AHUCoilRatingModeSerializer
 * :174-180 sources item.value) are used directly. Same numbers.
 */
export function applyCoilRequiredAwHeights(
  rows: Array<Record<string, any>>,
  s: Record<string, any>,
  cms: CoilCmsSettingsAw | null | undefined
): Array<Record<string, any>> {
  if (!rows || !rows.length) return rows;
  if (!cms || cms.min_clear_top == null || cms.coil_to_coil_vertical_clear == null) return rows;

  const fh = Number(s.fh);
  const casingFlangeTop = s.casing_flange_top == null || s.casing_flange_top === ''
    ? NaN : Number(s.casing_flange_top);
  const casingFlangeBtm = s.casing_flange_btm == null || s.casing_flange_btm === ''
    ? NaN : Number(s.casing_flange_btm);
  if (!fh || isNaN(casingFlangeTop) || isNaN(casingFlangeBtm)) return rows;
  const casingHeight = fh + casingFlangeTop + casingFlangeBtm;

  const qtyStacked = Number(s.qty_stacked);
  const hFromFloor = Number(s.h_from_floor);
  const mountingAngle = Number(s.mounting_angle);
  const minClearTop = Number(cms.min_clear_top);
  const coilToCoilVerticalClear = Number(cms.coil_to_coil_vertical_clear);

  if (!casingHeight || !qtyStacked || isNaN(hFromFloor) || isNaN(mountingAngle)) return rows;

  const angleRad = (Math.abs(mountingAngle) * Math.PI) / 180;
  return rows.map((coil) => {
    const depth = Number(String(coil.depth).replace(/,/g, ''));
    if (!depth && depth !== 0) return { ...coil, min_aw_height: null };
    return {
      ...coil,
      min_aw_height:
        (qtyStacked * casingHeight + (qtyStacked - 1) * coilToCoilVerticalClear) * Math.cos(angleRad) +
        depth * Math.sin(angleRad) +
        hFromFloor + minClearTop,
    };
  });
}

// ---------------------------------------------------------------------------
// ★★ Phase 6.5 (builder 4) — HEADER-MODE vendor rate input.
//
// WHY A SEPARATE BUILDER (and why the board one is untouched):
// `buildVendorRateInput` above is an ALLOW-LIST over the *board* state, which is
// seeded from the saved AHU coil + its section/model context. In HEADER mode
// there is no AHU (discovery §0), so ~40 of the members legacy ships were simply
// absent from the payload and the RAE vendor answered
// `15201 - Air face velocity (0 fpm …)` / `15204 - Invalid Requested Capacity`
// with **0 models** — the coil tab could not complete a rate at all.
//
// Legacy does NOT use an allow-list: `collectDataAndRate`
// (coil.selector.js:2761-2866) sweeps EVERY `.search_field` in the coil body and
// keys each one by `data-name || name`. This function is the header-mode mirror
// of that sweep, driven off the flat state + the loaded master lists instead of
// the DOM (React renders a reduced control set — the "absent controls" parity
// item stays parked — but the *state* carries every member, seeded from
// `HEADER_COIL_DEFAULTS` = legacy `coilDefaults` + the `options.header` override,
// coil.selector.js:257-383 + :394-420).
//
// The member list, the ORDER, and every value were matched against a LIVE
// legacy capture on dev3 `/sizer/` (header popup → Coil tab → RAE, default
// zero state, `POST /api/v1.0/rating/` → `ok:true`, 2 models):
// `.claude/skills/frontend-verify/.runtime/v65/b4_old.json` (68 `.search_field`
// elements → 63 input members; two independent runs byte-identical).
//
// The legacy sweep's exact coercions, reproduced below:
//  * SELECT → `option.data('value')` when the attribute exists, else `elem.val()`
//    (coil.selector.js:2812-2815). `data-value` IS the master row `.value`, so
//    resolving the state id against the loaded master list is equivalent.
//  * `'None'` → `'--'` (:2846).
//  * INPUT → `Number(val.replace(/,/g,''))`, empty ⇒ member DROPPED (:2826-2831).
//  * checkbox → `.prop('checked').toString()` ⇒ `'false'` / `'true'` (:2823).
//  * `tube_{vert,hor}_spacing_id` is special-cased at :2801-2808: it emits BOTH
//    `tube_*_spacing` (the raw jQuery `.data('value')`, i.e. a NUMBER — jQuery
//    coerces numeric data-* attrs) and `tube_*_spacing_id` (the id STRING), and
//    the generic tail skips the `_id` name (:2844).
//  * the four header/connection diameters are FORCED to `'--'` for every mfg
//    other than 143 / 79 (:2816-2823).
//  * `tube_wall_thick` falls back to the selected option TEXT then `'0.020'`
//    (`rate()`, :2929).
//  * `apd_max` defaults to the NUMBER 3 (`rate()`, :2880).
//
// ★ BOARD MODE CANNOT BE AFFECTED: this is a new exported function with no
// caller other than the `headerMode` branch of `CoilSelector.doRate`; the board
// branch still calls `buildVendorRateInput` unchanged. The A/B-proven board
// envelope (0 of 32 keys differ) is therefore byte-identical by construction.
// ---------------------------------------------------------------------------

/** Small master tables whose option lists the React coil form never loads
 * (their controls are not rendered — the parked "absent controls" item), so the
 * header sweep has no `.value` to resolve. Copied from the dev3 master tables
 * (`ksdm_master_ahu_hand`, `ksdm_master_ahu_coil_connection_end`,
 * `ksdm_master_ahu_piping_end`, `ksdm_master_ahu_coil_conn_rotation`,
 * `ksdm_master_ahu_coil_tube_cleanable`, `ksdm_master_ahu_pricing_method`,
 * `ksdm_master_ahu_coil_casing_flange_width`) — the id→value maps legacy's
 * `data-value` attributes are rendered from. `hands`, `connection_ends` and
 * `pricing_methods` are GLOBAL routes (collection/hands.js:8,
 * connection_ends.js:8, pricing_methods.js:8) so they are mfg-independent;
 * `piping_ends` / `conn_rotations` / `tube_cleanables` /
 * `casing_flange_widths` are mfg+type-filtered routes whose ids are stable
 * master-table ids, and header mode never changes the ids that address them
 * (they have no control). */
export const HEADER_COIL_STATIC_VALUES: Record<string, Record<string, string>> = {
  // conn_hand_id → data-name="conn_hand" (coil.selector.tmpl:466-468)
  conn_hand_id: { 1: 'left', 2: 'right' },
  // conn_end_id → data-name="conn_end"
  conn_end_id: { 1: 'same_end', 2: 'opp_end' },
  // conn_pipe_end_id → data-name="conn_pipe_end" (master piping_end)
  conn_pipe_end_id: {
    1: 'mpt', 2: 'fpt', 3: 'swt', 4: 'butt_weld', 5: 'victaulic',
    6: 'screw_thread_flange', 7: 'weld_neck_flange', 8: 'slip_on', 9: 'grooved',
    10: 'flanges_150',
  },
  // conn_rotation_id → data-name="conn_rotation"
  conn_rotation_id: {
    1: 'none', 2: 'lh_90deg_a_airflow', 3: 'lh_90deg_a_airflow_staggered_headers',
    4: 'lh_90deg_away_from_coil', 5: 'lh_90deg_w_airflow',
    6: 'lh_90deg_w_airflow_staggered_headers', 7: 'rh_90deg_a_airflow',
    8: 'rh_90deg_a_airflow_staggered_headers', 9: 'rh_90deg_away_from_coil',
    10: 'rh_90deg_w_airflow', 11: 'rh_90deg_w_airflow_staggered_headers',
  },
  // tube_cleanable_id → data-name="tube_cleanable"
  tube_cleanable_id: {
    1: 'cleanable_opp_end', 2: 'cleanable_supply_end', 3: 'cleanable_both_end',
  },
  // pricing_method_id — NOTE legacy keeps the `_id` suffix here because the
  // select carries NO data-name (coil.selector.tmpl:1746), so the sweep keys it
  // by `name`; the VALUE is still the master row's data-value.
  pricing_method_id: { 1: 'by_application', 2: 'by_user_cost', 3: 'by_user_price' },
  // the four casing-flange selects resolve to a WIDTH (inches)
  casing_flange_width: { 1: '0.5', 2: '0.75', 3: '1', 4: '1.25', 5: '1.5', 6: '2' },
};

export interface HeaderCoilRateArgs {
  /** the flat coil state (HEADER_COIL_DEFAULTS + user edits + cascade re-picks). */
  s: Record<string, any>;
  /** the loaded master option lists, keyed by field (masterListsRef.current). */
  masterLists: Record<string, Array<Record<string, any>>>;
  /** mfg_id — gates the forced `'--'` header/conn diameters (js:2816). */
  mfgId: number | string | null | undefined;
  /** coil_type string ('fluid' | 'evaporator' | 'condenser' | 'steam'). */
  coilType: string;
  /** the duty option's machine value (`resolveDutyValue()`). */
  duty: string;
  /** the independent-rating popup (no AHU) rather than a board coil dialog.
   * Only `vent_drain` reads it — see the force-set note at its emit site. */
  headerMode?: boolean;
}

/**
 * HEADER-MODE mirror of legacy `collectDataAndRate` + `rate()` for the VENDOR
 * route. Returns the `input` object of `POST /api/v1.0/rating/`.
 * Member order matches the live legacy capture (§ b4_old.json).
 */
export function buildHeaderVendorRateInput(args: HeaderCoilRateArgs): Record<string, unknown> {
  const { s, masterLists, mfgId, coilType, duty, headerMode } = args;
  const out: Record<string, unknown> = {};

  const isNone = (v: unknown) =>
    v == null || v === '' || v === 'None' || v === '--';

  /** resolve a state id against a loaded master list → the row `.value` CODE
   * (the analogue of `option.data('value')`). */
  const code = (field: string, idKey = field): string | undefined => {
    const id = s[idKey];
    if (isNone(id)) return undefined;
    const rows = masterLists[field];
    if (!rows || !rows.length) return undefined;
    const row = rows.find((r) => String(coilOptionId(r, field)) === String(id));
    return row ? coilOptionValue(row, field) : undefined;
  };
  /** resolve via the static id→value tables above. */
  const stat = (table: string, idKey: string): string | undefined => {
    const id = s[idKey];
    if (isNone(id)) return undefined;
    return HEADER_COIL_STATIC_VALUES[table]?.[String(id)];
  };
  /** a SELECT member: `'--'` whenever the value resolves to None (js:2846).
   * `undefined` (an unresolvable value — usually a master list that has not
   * landed) is DROPPED rather than dashed, so a slow catalog never injects a
   * bogus `'--'` where legacy would have shipped a real code. */
  const put = (key: string, v: unknown) => {
    if (v === undefined) return;
    out[key] = isNone(v) ? '--' : String(v);
  };
  /** the same, for the selects whose value is LEGITIMATELY unset in the zero
   * state: legacy renders an explicit `- -` option (`value="None"`, or
   * `data-value="None"` for circuiting — coil.selector.tmpl) and the sweep maps
   * it to `'--'` (js:2846), so these members are ALWAYS present. */
  const putDash = (key: string, v: unknown) => {
    out[key] = v === undefined || isNone(v) ? '--' : String(v);
  };
  /** an INPUT member: numeric coercion, EMPTY ⇒ dropped (js:2826-2831). */
  const num = (key: string, v: unknown, fallback?: number) => {
    let raw = v;
    if (raw == null || raw === '') raw = fallback;
    if (raw == null || raw === '') return;
    const n = Number(String(raw).replace(/,/g, ''));
    if (!Number.isFinite(n)) return;
    out[key] = String(n);
  };
  /** a checkbox member: `'false'` / `'true'` (js:2823). */
  const bool = (key: string, v: unknown) => {
    out[key] = v === true || v === 'true' ? 'true' : 'false';
  };

  const vis = coilTypeVisibility(coilType);

  // ---- identity / catalog selection -------------------------------------
  put('mfg_id', mfgId);
  put('type', code('type_id') ?? coilType);
  put('duty', duty);
  // htfluid ships for EVERY coil type: the select is rendered unconditionally
  // and always carries `search_field` — its class gate is `{{#ifCompare
  // coil_type '>' 'steam'}}` (tmpl:109), and none of fluid/evaporator/steam/
  // condenser sorts after 'steam', so the `{{else}}` branch always wins. Gating
  // this on the fluid type sent an evaporator's rate with no refrigerant.
  put('htfluid', code('htfluid_id'));
  num('qty_stacked', s.qty_stacked);
  num('fh', s.fh);
  num('fl', s.fl);
  putDash('rows', code('rows_id'));
  putDash('fpi', code('fpi_id'));
  // ★ Style. The `style_id` select (tmpl:240) carries `search_field` and sits
  // inside `{{#ifCompare coil_type '!=' 'fluid'}}` (tmpl:232), so the legacy
  // sweep ships it for evaporator / condenser / steam but never for fluid —
  // exactly the set Modine's mapping accepts `coil_style` for (modine/
  // mapping.py:527 evaporator, :1037 steam, :1224 condenser; absent from the
  // fluid block). Its data-name flips to "style" for mfg 27, else "coil_style".
  //
  // This member was MISSING from the builder entirely, so every non-fluid
  // vendor rate went out with no `Style` parameter. Against the live Modine
  // endpoint that is not a silent default — it is fatal: the same evaporator
  // payload returns `ok:false, errors:['Got error from the API.']` with 0
  // models without the key and `ok:true` with 64 models with it.
  //
  // Legacy has no "unset" state here: there is no `- -` option, so when
  // `params_coil.style_id` matches no row the browser selects the FIRST option
  // and `option.data('value')` yields `styles[0].item.value`. Mirror that
  // EFFECTIVE value rather than dropping the key (the same value-space slip
  // already handled for fl/fh at :1323).
  if (!vis.fluidFlow) {
    const styleRows = masterLists['style_id'];
    const styleCode =
      code('style_id') ??
      (styleRows?.length ? coilOptionValue(styleRows[0], 'style_id') : undefined);
    put(String(mfgId) === '27' ? 'style' : 'coil_style', styleCode);
  }
  putDash('circuiting', code('circuiting_id'));
  // the `- -` option of the non-DC feeds select carries data-value="0"
  // (coil.selector.tmpl:287), so an unset feeds_id ships "0" — NOT "--".
  // mfg 181 (Direct Coil) renders the DC-feeds variant instead, whose `- -`
  // option carries data-value="--" (tmpl:273-281).
  const feedsUnset = String(mfgId) === '181' ? '--' : '0';
  out.feeds = isNone(s.feeds_id) ? feedsUnset : (code('feeds_id') ?? feedsUnset);
  // ★ tube_dia is the ONE search-field select whose `data-value` is NOT the
  // master row `.value` — the template picks the column by manufacturer
  // (coil.selector.tmpl:309-318):
  //     mfg 27 or 190 → data-value="{{item.value}}"   ("0.625")
  //     everything else → data-value="{{item.name}}"  ("5/8")
  // The BUG-3 sweep generalised `.value` to every select, so mfg 79 (Modine)
  // and the other non-27/190 vendors were sent "0.625" where dev sends "5/8".
  // Modine rejects it outright — the live endpoint answers
  // `Invalid input value 0.625 for parameter TubeOD.` with 0 models.
  const tubeDiaRows = masterLists['tube_dia_id'];
  const tubeDiaRow =
    !isNone(s.tube_dia_id) && tubeDiaRows
      ? tubeDiaRows.find(
          (r) => String(coilOptionId(r, 'tube_dia_id')) === String(s.tube_dia_id),
        )
      : undefined;
  const tubeDiaByName = String(mfgId) !== '27' && String(mfgId) !== '190';
  put(
    'tube_dia',
    tubeDiaRow && tubeDiaByName
      ? coilOptionLabel(tubeDiaRow, 'tube_dia_id')
      : code('tube_dia_id'),
  );
  // tube_wall_thick: '' from the sweep, then rate()'s option-text/'0.020' fallback.
  put('tube_wall_thick', code('tube_wall_thick_id') ?? '0.020');
  put('tube_mtl', code('tube_mtl_id'));
  put('fin_mtl', code('fin_mtl_id'));
  put('fin_finish', code('fin_finish_id'));
  put('fin_thick', code('fin_thick_id'));
  put('fin_pattern', code('fin_pattern_id'));
  // NOTE the key keeps the `_gauge` suffix on the VENDOR route: the select's
  // data-name IS "casing_mtl_gauge" (coil.selector.tmpl), so the sweep's
  // `casing_mtl`/`casing_gauge` split (js:2789-2797) never fires and the
  // `casing_mtl_gauge → casing_mtl` rename at js:2714 is DB-route only.
  put('casing_mtl_gauge', code('casing_mtl_id'));
  put('conn_hand', stat('conn_hand_id', 'conn_hand_id') ?? 'left');

  // ---- air side ---------------------------------------------------------
  // ★ THE DEFECT THIS BUILDER CLOSES: legacy ships `airflow_actual_total`, NOT
  // `airflow_actual`. Only the TOTAL box carries `search_field`
  // (coil.selector.tmpl:527-528); the per-coil `airflow_actual` box is
  // `per_coil_field required_field` (tmpl:520-522) and never reaches the
  // vendor. The header port sent `airflow_actual`, so the vendor saw no
  // airflow → face velocity 0 → 0 models.
  num('airflow_actual_total', s.airflow_actual_total);
  // air_stream_id: the select has NO data-value (tmpl:540) ⇒ raw option value.
  put('air_stream_id', s.air_stream_id);
  num('face_velocity_actual', s.face_velocity_actual);
  num('eadb_temp', s.eadb_temp);
  // ★ the WB pair is gated `coil_type != 'steam'` AND `duty == 'cooling'`
  // (tmpl:625-626 / :649-653) — a steam coil has no wet-bulb row at all, and a
  // heating coil's is not rendered. They were emitted unconditionally, so a
  // steam rate carried two members dev never sends (Super Radiator consumes
  // both).
  const wetBulb = !vis.steam && String(duty ?? '').toLowerCase() === 'cooling';
  if (wetBulb) num('eawb_temp', s.eawb_temp);
  num('desired_ladb_temp', s.desired_ladb_temp);
  if (wetBulb) num('desired_lawb_temp', s.desired_lawb_temp);
  num('desired_capacity_total', s.desired_capacity_total);
  // ★ `desired_capacity_total_total` (tmpl:701) is an unconditional
  // `search_field` that dev sweeps in EVERY combination and this builder never
  // emitted. Super Radiator consumes it.
  num('desired_capacity_total_total', s.desired_capacity_total_total);
  // `elevation` → data-name="altitude"; the template renders
  // `{{#if project.elevation}}…{{else}}0{{/if}}` (tmpl:732) and header mode has
  // NO project (discovery §0) ⇒ always 0.
  num('altitude', s.elevation, 0);

  // ---- fluid side (coil_type == 'fluid' blocks) --------------------------
  if (vis.fluidFlow) {
    num('entering_fluid_temp', s.entering_fluid_temp);
    num('desired_leaving_fluid_temp', s.desired_leaving_fluid_temp);
    // ★ EITHER/OR with the LFT above: with a leaving fluid temperature set the
    // template renders both flow boxes EMPTY (tmpl:945/:948
    // `{{#unless model.desired_leaving_fluid_temp}}`) while KEEPING the model
    // values, so dev's search-field sweep reads '' and ships neither. Sending
    // both is what a vendor rejects as over-constrained.
    if (s.desired_leaving_fluid_temp == null || s.desired_leaving_fluid_temp === '') {
      num('fluid_flow', s.fluid_flow);
      // ★ the TOTAL box is a fluid-only `search_field` too (tmpl:948) and dev
      // sweeps it. No migrated vendor reads it today, but it keeps the fluid
      // payload identical to dev's.
      num('fluid_flow_total', s.fluid_flow_total);
    }
    num('fouling_factor_inside', s.fouling_factor_inside);
    num('fouling_factor_outside', s.fouling_factor_outside);
    // Selector-parity — glycol concentration is a fluid-coil `.search_field`
    // (coil.selector.tmpl:124); the legacy sweep ships it. Empty ⇒ dropped.
    // For WATER the template renders the input with NO `value` attribute and
    // `non-clickable` (tmpl:129-134), so the sweep reads '' and drops the key —
    // a captured dev payload on water has no `glycol_percent` at all. React
    // holds a real 0 in state, so gate on the fluid code rather than emptiness.
    if (String(code('htfluid_id') ?? '').toLowerCase() !== 'water') {
      num('glycol_percent', s.glycol_percent);
    }
  }
  // ---- refrigerant / condenser / steam numerics (state-seeded per
  //      DEFAULTS_DEPENDS_ON_DUTY; only present for their own coil types) ----
  // ★ `tube_vapor_velocity` sits inside `{{#unless external_rating}}`
  // (tmpl:869 → :878), so it is NOT in the DOM for a vendor rate and dev never
  // sweeps it. It used to be emitted unconditionally for an evaporator.
  //
  // ★ The refrigerant/condenser UNION is now SPLIT — the follow-up the previous
  // note parked. The template gates the two groups separately and they overlap
  // only on `ref_liquid_temp`:
  //   evaporator (tmpl:841): suction_temp, ref_liquid_temp, entering_superheat
  //   condenser  (tmpl:763): vapor_temp, ref_liquid_temp, condensing_temp,
  //                          subcooling_temp, percent_condensing_load
  // The union was NOT inert: Super Radiator consumes condensing_temp and
  // subcooling_temp on an EVAPORATOR, and Modine consumes suction_temp on a
  // CONDENSER, so each vendor saw members dev never sends for that type.
  //
  // The evaporator instances carry TEMPLATE DEFAULTS, like entering_superheat's 8:
  //   tmpl:850  suction_temp    value="{{#if model.suction_temp}}…{{else}}45{{/if}}"
  //   tmpl:863  ref_liquid_temp value="{{#if model.ref_liquid_temp}}…{{else}}110{{/if}}"
  // The CONDENSER instance of ref_liquid_temp has NO default
  // (tmpl:783 `value="{{model.ref_liquid_temp}}"`), so the fallbacks stay
  // evaporator-only.
  if (vis.refrigerant) {
    num('suction_temp', s.suction_temp, 45);
    num('ref_liquid_temp', s.ref_liquid_temp, 110);
  }
  if (vis.condenser) {
    num('vapor_temp', s.vapor_temp);
    num('ref_liquid_temp', s.ref_liquid_temp);
    num('condensing_temp', s.condensing_temp);
    num('subcooling_temp', s.subcooling_temp);
    num('percent_condensing_load', s.percent_condensing_load);
  }
  // `entering_superheat` is rendered TWICE, and this builder only ever saw the
  // steam one: `{{#ifCompare coil_type '==' 'steam'}}` (tmpl:710 → :719) AND
  // `{{#ifCompare coil_type '==' 'evaporator'}}` (tmpl:841 → :895). Both render
  // `value="{{#if model.entering_superheat}}…{{else}}8{{/if}}"`, so the legacy
  // sweep always ships a number for an evaporator. Modine's evaporator block
  // consumes it as `SuperHeatTemp` (mapping.py:477) and it MOVES the selection:
  // on the same payload the vendor returns 10-row models with it and 5-row
  // models without, so dropping it silently changed every rated capacity.
  // (Condenser is deliberately NOT included — no template instance renders it
  // for a condenser, so dev does not send it there either.)
  if (vis.steam || vis.refrigerant) num('entering_superheat', s.entering_superheat, 8);
  // ★ `leaving_superheat` is DROPPED entirely. Its only instance is inside
  // `coil_type == 'evaporator'` AND `{{#unless external_rating}}` (tmpl:841 →
  // :869 → :900), so it is never in the DOM for a vendor rate — not for an
  // evaporator, and not for steam either (the steam block has no such input).
  // Emitting it under `vis.steam` was a member dev never sends.
  // ★ STEAM-ONLY members this builder never emitted at all (tmpl:732 / :1598 /
  // :1613, all `coil_type == 'steam'`). `steam_pressure` is the critical one:
  // Modine maps it to `Pressure` and WITHOUT it the vendor rejects every steam
  // rate outright — `Invalid Gauge Pressure: (2 - 350 psi)`, 0 models. So steam
  // coils could not be rated on the React app at all. RAE and Super Radiator
  // consume it too. `vacuum_breaker` is consumed by Modine steam.
  if (vis.steam) {
    num('steam_pressure', s.steam_pressure, 5);
    bool('vacuum_breaker', s.vacuum_breaker);
    bool('thermostatic_vent', s.thermostatic_vent);
  }
  // ★ EVAPORATOR-only members (tmpl:140 / :1684 / :1698). No migrated vendor
  // consumes these today (only CoilMaster, which is short-circuited before the
  // API call), but dev sweeps them and they are cheap to carry, so the payload
  // matches for every combination rather than only the ones we can measure.
  if (vis.refrigerant) {
    put('evaporator_mode', code('evaporator_mode_id'));
    bool('txv', s.txv);
    bool('txv_instl', s.txv_instl);
  }
  // ★ mfg 79 renders a Label Kit checkbox (tmpl:1728) and Modine consumes it.
  if (String(mfgId) === '79') bool('label_kit', s.label_kit);

  // ---- headers / connections -------------------------------------------
  put('header_mtl', code('header_mtl_id'));
  // js:2816-2823 — for every mfg other than 143 / 79 the four diameters are
  // reset in the DOM and forced to '--' in the payload.
  const forceDash = String(mfgId) !== '143' && String(mfgId) !== '79';
  if (forceDash) {
    out.outlet_header_dia = '--';
    out.outlet_conn_dia = '--';
    out.inlet_header_dia = '--';
    out.inlet_conn_dia = '--';
  } else {
    // ★ putDash, not put. For mfg 143 / 79 dev keeps whatever the four selects
    // hold, and their `- -` option is `value="None"` → the sweep maps it to
    // '--' (js:2846). `put` DROPPED the member whenever the id was unset or the
    // catalog had not landed, so Super Radiator (143) rated with no header/
    // connection diameters at all where dev always sends four values.
    putDash('outlet_header_dia', code('outlet_header_dia'));
    putDash('outlet_conn_dia', code('outlet_conn_dia_id'));
    putDash('inlet_header_dia', code('inlet_header_dia'));
    putDash('inlet_conn_dia', code('inlet_conn_dia_id'));
  }
  put('conn_mtl', code('conn_mtl_id'));
  put('conn_end', stat('conn_end_id', 'conn_end_id'));
  put('conn_pipe_end', stat('conn_pipe_end_id', 'conn_pipe_end_id'));
  num('conn_protrusion_from_header', s.conn_protrusion_from_header);
  // static 1|2 options, no data-value, browser-selected first (tmpl:1228-1230).
  // ★ gated `coil_type != 'steam'` (tmpl:1245) — it was emitted for steam too.
  if (!vis.steam) put('connection_per_header', s.connection_per_header ?? 1);
  // ★ `conn_rotation` is inside the FLUID block (tmpl:1261); it was emitted for
  // every type.
  if (vis.fluidFlow) put('conn_rotation', stat('conn_rotation_id', 'conn_rotation_id'));

  // ---- mounting / casing ------------------------------------------------
  num('h_from_floor', s.h_from_floor);
  num('mounting_angle', s.mounting_angle);
  put('casing_type', code('casing_type_id'));
  // ★ the two SIDE flange selects carry NO data-name, so the sweep keys them by
  // `name` — i.e. the payload members keep the `_id` suffix while their VALUES
  // are the flange WIDTHS. The top/btm selects DO carry data-name (…_top/_btm).
  // ★ ...EXCEPT for mfg 181 (Direct Coil), whose `name` attribute drops the
  // `_id` suffix (tmpl:1408 / :1426 render `name="casing_flange_header_side"`
  // inside `{{#ifCompare params_coil.mfg_id '==' '181'}}`). Direct Coil's
  // handler reads the un-suffixed keys (search_product_bl.py), so the suffixed
  // ones were invisible to it.
  const sideSuffix = String(mfgId) === '181' ? '' : '_id';
  put(`casing_flange_header_side${sideSuffix}`, stat('casing_flange_width', 'casing_flange_header_side_id'));
  put(`casing_flange_return_side${sideSuffix}`, stat('casing_flange_width', 'casing_flange_return_side_id'));
  put('casing_flange_top', stat('casing_flange_width', 'casing_flange_top_id'));
  put('casing_flange_btm', stat('casing_flange_width', 'casing_flange_btm_id'));
  bool('mounting_holes', s.mounting_holes);

  // ---- tube spacings (the js:2801-2808 double-emit; the VALUE is a NUMBER) --
  for (const [idKey, valKey] of [
    ['tube_vert_spacing_id', 'tube_vert_spacing'],
    ['tube_hor_spacing_id', 'tube_hor_spacing'],
  ] as const) {
    const v = s[valKey] ?? code(idKey);
    if (v != null && v !== '') {
      const n = Number(String(v).replace(/,/g, ''));
      out[valKey] = Number.isFinite(n) ? n : String(v);
    }
    if (!isNone(s[idKey])) out[idKey] = String(s[idKey]);
  }

  // ---- limits / options -------------------------------------------------
  // fpi_max_id addresses the SAME master fpi table as fpi_id
  // (collection/fpis.js:8 — one route), so the loaded fpi_id list resolves it.
  put('fpi_max', code('fpi_id', 'fpi_max_id'));
  // `{{#if model.fluid_pd_max}}…{{else}}20{{/if}}` (tmpl:1567).
  if (vis.fluidFlow) num('fluid_pd_max', s.fluid_pd_max, 20);
  // ★ `tube_interior` (tmpl:1648) and `tube_cleanable` (tmpl:1664) both live in
  // the FLUID-only block; they were emitted for every type. Direct Coil consumes
  // tube_interior and Super Radiator consumes tube_cleanable, so both vendors
  // saw members dev never sends on an evaporator / condenser / steam coil.
  // ★ `vent_drain` — BOARD ONLY. The select is a `search_field` for mfg 27 and
  // nothing else (coil.selector.tmpl:1629's `{{#ifCompare params_coil.mfg_id
  // '==' '27'}}`), and it lives in the fluid-only Options block, so mfg 27 +
  // fluid is the entire set of payloads dev ever puts it in.
  //
  // What drops it in HEADER mode is the force-set at coil.selector.js:1823-1825:
  // `if (mfg_id == 181 && $('#ahu-Coil_Properties_Performance select[name=
  // "type_id"]').val() == 1) || $('#Coil_Properties_Performance select[name=
  // "type_id"]').val() == 1) $('#vent_drain_id').val('4')`. The second disjunct
  // is un-parenthesised and its container id carries NO `ahu-` prefix, so it is
  // the HEADER pane — true for every fluid coil there, whatever the mfg. The
  // mfg-27/type-1 vent_drains list is option-valued on `item.id` ⇒ 1/2/3, so
  // `'4'` matches nothing, jQuery `.val()` returns null and the sweep drops the
  // member. On the BOARD that pane is `#ahu-Coil_Properties_Performance`, the
  // disjunct is `undefined == 1` ⇒ false, no force-set runs and dev ships
  // `vent_drain: 'conn_end'` — which RAE consumes (it is absent from the
  // response's `skipped_input_parameter` list). The port omitted the member
  // unconditionally off a header-mode-only capture.
  if (vis.fluidFlow && !headerMode && String(mfgId) === '27') {
    putDash('vent_drain', code('vent_drain_id'));
  }
  if (vis.fluidFlow) {
    put('tube_interior', code('tube_interior_id'));
    putDash('tube_cleanable', stat('tube_cleanable_id', 'tube_cleanable_id'));
  }
  // ★ gated `coil_type != 'steam'` (tmpl:1712).
  if (!vis.steam) bool('moisture_eliminator', s.moisture_eliminator);
  bool('nitrogen_charge', s.nitrogen_charge);
  put('pricing_method_id', stat('pricing_method_id', 'pricing_method_id'));
  // (`vent_drain` is emitted above — board only; see the note there.)

  // rate(): `if (!self.data.hasOwnProperty('apd_max')) self.data.apd_max = 3;`
  // — a NUMBER, and the `apd_max` INPUT is empty in the zero state (js:2880).
  num('apd_max', s.apd_max);
  if (!('apd_max' in out)) out.apd_max = 3;

  return out;
}

/** Build the KSDM-DB ('manual') input (self.rate_data): numeric coercion. */
export function buildDbRateInput(s: CoilRateState): Record<string, unknown> {
  const input: Record<string, unknown> = {};
  for (const k of COIL_DB_INPUT_FIELDS) {
    if (s[k] != null && s[k] !== '') input[k] = String(Number(String(s[k]).replace(/,/g, '')));
  }
  // also pass the catalog filter selection (rows/fpi/etc come via DB_INPUT).
  if (!('apd_max' in input)) input.apd_max = 3;
  return input;
}

// ---------------------------------------------------------------------------
// the FOUR-WAY selectCoil write payload (§33.2 / coil.selector.js:3230-3547).
// PURE so the verifier can read the exact write shapes. txv uses the explicit
// boolean parse ([NEG]#80 — NOT eval).
// ---------------------------------------------------------------------------

/** Explicit boolean parse for txv (replaces legacy `eval(self.data['txv'])`,
 * [NEG]#80). self.data['txv'] held 'true'/'false' strings (checkbox .toString). */
export function parseTxv(raw: unknown): boolean {
  if (raw === true) return true;
  if (raw === false || raw == null) return false;
  const s = String(raw).trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'on' || s === 'yes';
}

const numOrNull = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isNaN(n) ? null : n;
};

/**
 * Selector-parity — legacy `getParamIdByNumberValue` (coil.selector.js:2149).
 * Maps a numeric VALUE (the rated coil's returned diameter) back to its
 * connection_diameters row id; returns null when unmatched (legacy 'None').
 * Used for the Manifold inlet/outlet CONNECTION diameter ids on Select
 * (coil.selector.js:3713/:3735 — the saved id comes from the vendor result,
 * NOT the user's select). Rows may be wrapped {item:{id,value}} or flat.
 */
export function getParamIdByNumberValue(
  collection: Array<Record<string, any>> | null | undefined,
  value: unknown
): number | null {
  if (value == null || value === '' || collection == null) return null;
  let v = value;
  if (typeof v === 'string' && v.includes('"')) v = v.slice(0, -1);
  const hit = collection.find((row) => {
    const item = (row as any).item ?? row;
    return Number(v) === Number(item.value);
  });
  if (!hit) return null;
  const item = (hit as any).item ?? hit;
  return numOrNull(item.id);
}

export interface CoilSelectPayload {
  /** (1) section.set */
  sectionSave: Record<string, unknown>;
  /** (2) project.save (elevation rounded; custom_job/quote/report_logo undefined) */
  projectSave: Record<string, unknown>;
  /** (3) current_model.set (~55 perf fields) + the explicit patch */
  currentModelSet: Record<string, unknown>;
  currentModelPatch: Record<string, unknown>;
  /** (4) rated_coil_model.set → PATCH rating_mode_coils/<id> */
  ratedCoilSave: Record<string, unknown>;
}

/**
 * Build the four-way selectCoil write payload. `s` = the state model
 * (model + params_coil merged), `rated` = the chosen rated_coil row, `section`
 * = the section JSON, `project` = the project JSON, `mountingAngle` = the DOM
 * mounting-angle value, `externalRating` = the rate path used (false ⇒ DB).
 */
export function buildCoilSelectPayload(args: {
  s: Record<string, any>;
  rated: Record<string, any>;
  section: Record<string, any>;
  project: Record<string, any>;
  mountingAngle: number | string | null;
  externalRating: boolean | undefined;
  genericMfg: boolean;
  txvRaw: unknown;
  coilParams: Record<string, unknown>;
  hasCircuitings: boolean;
  hasFeeds: boolean;
  /** Selector-parity — the loaded connection_diameters list, so the Manifold
   * inlet/outlet CONNECTION diameter ids can be resolved from the rated result
   * (coil.selector.js:3713/:3735 getParamIdByNumberValue). */
  connectionDiameters?: Array<Record<string, any>> | null;
  /** Selector-parity — the loaded rows / fpi option lists, so the rated
   * result's ROWS and FIN SPACING resolve back to their ids on Select
   * (coil.selector.js:3717/:3757 `getParamIdByNumberValue(self.collections.
   * rows|fpis, self.rated_coil['rows'|'fpi'])`). Without them `rows_id` /
   * `fpi_id` stay null and the component form's "Rows / FPI" line renders
   * empty — the serializer's `rows`/`fpi` are read-only projections of those
   * two FKs (coil_serializers.py:224-225), not stored strings. */
  rowsList?: Array<Record<string, any>> | null;
  fpisList?: Array<Record<string, any>> | null;
  /** Selector-parity — the rate response's TOP-LEVEL notes array
   * (`self.NNotes = incoming_data.notes`, coil.selector.js:3226), joined with
   * '\n' onto the rated row. Legacy's literal seeds `notes: self.rated_coil
   * ['notes']` and then RE-DECLARES `notes: self.NNotes` 40 lines later
   * (:3743) — last one wins, so the per-ROW key never reaches the server and
   * the top-level one always does. */
  apiNotes?: string[] | null;
  /** Dev-sync J6 — the staged section season (coil.selector.js:3463) + its
   * CMS-resolved value string ('summer'/'winter'/'summer_winter'; the
   * per-season copy keys on the VALUE, not the hardcoded ids). */
  seasonId?: number | null;
  seasonValue?: string | null;
}): CoilSelectPayload {
  const { s, rated, section, project, mountingAngle, externalRating, genericMfg, txvRaw, coilParams, hasCircuitings, connectionDiameters, rowsList, fpisList, apiNotes, seasonId, seasonValue } = args;

  // ----- (1) section.set -----
  // Dev-sync H: legacy coerces the pair with Number() before the section PATCH
  // (coil.selector.js:3268-3271 on merged dev) — the inputs hold strings.
  //
  // Dev-sync J6 (coil.selector.js:3463-3485): season_id rides the section
  // save, and the dialog's SINGLE entering pair is copied into whichever
  // per-season slot the Season select names — Summer → the _summer aliases,
  // Winter → the _winter columns; Summer+Winter writes only the bare pair
  // (server-side ≡ the summer alias), leaving winter untouched. Legacy keys
  // the copy on the hardcoded ids 1/2; here it keys on the CMS row's value.
  // The `if (!seasonId)` duty fallback at :3467-3473 IS ported — but upstream,
  // in CoilSelector's seasonIdForSaveFallback, where the CMS rows and the duty
  // are both in scope; `seasonId` already arrives resolved here. It was
  // previously dropped as dead on the grounds that `duty_id === 1` compares a
  // number against a STRING from elem.val(); that only holds after the user
  // touches the Duty select, and on open duty_id is a number (:1109/:1118/
  // :4620), so the branch is live for exactly the case it exists to cover — a
  // section whose season was never set. Dropping it persisted `season_id:
  // null` where dev persists 1/2, and with it skipped the per-season copy
  // below, since both branches are gated on a non-null season.
  //
  // Sync K note (dev 9ab54f1b01 + backend 31e3a510f8): with a HEATING coil on
  // Season = Summer the bare pair below carries the rated (summer-effective)
  // values and the _winter columns are deliberately NOT written — AHUCoil's
  // eadb/eawb properties read the bare pair directly for that case
  // (_heating_season_is_summer), and the backend's unconditional bare→winter
  // mirror on coil save is skipped for it too, so nothing overwrites them
  // server-side either. Winter (or unset season, the legacy default) keeps
  // the winter copy; the backend mirror re-syncs it on every heating save
  // regardless.
  const seasonIdNum = Number(seasonId) || null;
  const sectionSave: Record<string, unknown> = {
    season_id: seasonIdNum,
    eadb_temp: Number(section.eadb_temp),
    eawb_temp: Number(section.eawb_temp),
  };
  if (seasonIdNum != null && seasonValue === 'summer') {
    sectionSave.eadb_temp_summer = sectionSave.eadb_temp;
    sectionSave.eawb_temp_summer = sectionSave.eawb_temp;
  } else if (seasonIdNum != null && seasonValue === 'winter') {
    sectionSave.eadb_temp_winter = sectionSave.eadb_temp;
    sectionSave.eawb_temp_winter = sectionSave.eawb_temp;
  }

  // ----- (2) project.save (elevation rounded; explicit undefineds) -----
  const projectSave: Record<string, unknown> = {
    elevation: project.elevation != null ? Math.round(Number(String(project.elevation).replace(/,/g, ''))) : null,
    custom_job_number: undefined,
    custom_quote_number: undefined,
    report_logo: undefined,
  };

  // DB-route stamps tube_wall_thick into coil_params (legacy 3246).
  const params = { ...coilParams };
  if (externalRating === false && s.tube_wall_thick != null) {
    params.tube_wall_thick = s.tube_wall_thick;
  }
  params.label_kit = s.label_kit ?? false;
  params.nitrogen_charge = s.nitrogen_charge ?? false;

  // ----- (3) current_model.set (~55 perf fields) -----
  const currentModelSetRaw: Record<string, unknown> = {
    eadb_temp: section.eadb_temp,
    eawb_temp: section.eawb_temp,
    evaporator_mode_id: s.evaporator_mode_id,
    capacity_total: rated.capacity_total,
    capacity_sensible: rated.capacity_sensible,
    glycol_percent: s.glycol_percent,
    qty_stacked: s.qty_stacked,
    duty_id: s.duty_id,
    entering_fluid_temp: s.entering_fluid_temp,
    fluid_pd_max: s.fluid_pd_max,
    conn_protrusion_from_header: s.conn_protrusion_from_header,
    airflow_actual: numOrNull(s.airflow_actual_total),
    face_velocity_actual: s.face_velocity_actual,
    face_velocity_std: rated.face_velocity_std,
    htfluid_id: s.htfluid_id,
    fouling_factor_inside: s.fouling_factor_inside,
    fouling_factor_outside: s.fouling_factor_outside,
    fpi_max_id: s.fpi_max_id,
    desired_capacity_total: s.desired_capacity_total ?? null,
    desired_ladb_temp: s.desired_ladb_temp ?? null,
    desired_lawb_temp: s.desired_lawb_temp ?? null,
    desired_leaving_fluid_temp: numOrNull(s.desired_leaving_fluid_temp),
    refrigerant_charge: rated.refrigerant_charge,
    steam_pressure: s.steam_pressure,
    fluid_internal_volume: rated.fluid_internal_volume,
    entering_superheat: s.entering_superheat,
    vapor_temp: s.vapor_temp,
    condensing_temp: s.condensing_temp,
    subcooling_temp: s.subcooling_temp,
    percent_condensing_load: s.percent_condensing_load,
    // [NEG]#80 — explicit boolean parse, NOT eval.
    txv: parseTxv(txvRaw),
    txv_instl: s.txv_instl,
    suction_temp: s.suction_temp,
    ref_liquid_temp: s.ref_liquid_temp,
    leaving_superheat: rated.leaving_superheat,
    ladb_temp: rated.ladb_temp ?? rated.desired_ladb_temp ?? s.desired_ladb_temp ?? null,
    lawb_temp: rated.lawb_temp ?? rated.desired_lawb_temp ?? s.desired_lawb_temp ?? null,
    apd: rated.apd,
    apd_max: rated.apd_max,
    fluid_flow: rated.fluid_flow,
    leaving_fluid_temp: rated.leaving_fluid_temp,
    fluid_velocity: rated.suction_line_velocity ?? rated.fluid_velocity,
    fluid_pd: rated.fluid_pd,
    condensate_load_per_circuit: rated.condensate_load_per_circuit,
    condensate_load_per_circuit_all: rated.condensate_load_per_circuit_all,
    tube_vapor_velocity: rated.tube_vapor_velocity,
    reynolds_number: rated.reynolds_number,
    pricing_method_id: s.pricing_method_id,
    cost_product_user: s.cost_product_user,
    cost_freight_user: s.cost_freight_user,
    net_price_product_user: s.net_price_product_user,
    net_price_freight_user: s.net_price_freight_user,
    generic_mfg: genericMfg,
    air_stream_id: s.air_stream_id,
    mounting_angle: s.mounting_angle,
    h_from_floor: s.h_from_floor,
    circuiting_id: hasCircuitings ? numOrNull(s.circuiting_id) : null,
    connection_per_header: s.connection_per_header,
    tube_vert_spacing_id: s.tube_vert_spacing_id,
    tube_hor_spacing_id: s.tube_hor_spacing_id,
  };
  // numeric coercion except txv/txv_instl/generic_mfg (capacities rounded).
  const currentModelSet: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(currentModelSetRaw)) {
    if (k === 'txv' || k === 'txv_instl' || k === 'generic_mfg') {
      currentModelSet[k] = v;
    } else if (v != null && v !== '') {
      let n = Number(String(v).replace(/,/g, ''));
      if (k.indexOf('capacity') !== -1 && !Number.isNaN(n)) n = Math.round(n);
      currentModelSet[k] = Number.isNaN(n) ? v : n;
    } else {
      currentModelSet[k] = v;
    }
  }

  // the explicit patch (legacy 3530): {mounting_angle, duty_id}.
  const currentModelPatch: Record<string, unknown> = {
    mounting_angle: mountingAngle,
    duty_id: s.duty_id,
  };

  // ----- (4) rated_coil_model.set → PATCH rating_mode_coils/<id> -----
  const STRING_OR_BOOL_KEYS = new Set([
    'casing_stacking_flange', 'model', 'mounting_holes', 'vacuum_breaker',
    'thermostatic_vent', 'nitrogen_charge', 'txv_instl', 'txv', 'moisture_eliminator',
    'rating_datetime', 'tube_cleanable', 'circuiting_rating_output', 'coil_params',
    'aux_side_conn', 'cost_registry_key',
  ]);
  const ratedRaw: Record<string, unknown> = {
    conn_protrusion_from_header: s.conn_protrusion_from_header,
    aux_side_conn: s.aux_side_conn,
    casing_flange_btm_id: s.casing_flange_btm_id,
    casing_flange_header_side_id: s.casing_flange_header_side_id,
    casing_flange_return_side_id: s.casing_flange_return_side_id,
    casing_flange_top_id: s.casing_flange_top_id,
    casing_height: rated.casing_height,
    casing_length: rated.casing_length,
    casing_mtl_id: s.casing_mtl_id,
    casing_stacking_flange: s.casing_stacking_flange,
    casing_type_id: s.casing_type_id,
    conn_end_id: s.conn_end_id,
    conn_rotation_id: Number(s.type_id) === 1 ? s.conn_rotation_id : null,
    conn_hand_id: s.conn_hand_id,
    conn_mtl_id: s.conn_mtl_id,
    conn_pipe_end_id: s.conn_pipe_end_id,
    depth: rated.depth,
    distributor_1_dia: rated.distributor_1_dia,
    distributor_2_dia: rated.distributor_2_dia,
    distributor_3_dia: rated.distributor_3_dia,
    distributor_4_dia: rated.distributor_4_dia,
    fastening_method_id: s.fastening_method_id,
    fh: s.fh,
    fin_finish_id: s.fin_finish_id,
    fin_mtl_id: s.fin_mtl_id,
    fin_pattern_id: s.fin_pattern_id,
    fin_thick_id: s.fin_thick_id,
    fl: s.fl,
    // Selector-parity — ROWS and FIN SPACING: like the Manifold conn-dia ids
    // above, the SAVED id is derived from the VENDOR's returned value mapped
    // back through the loaded option list (coil.selector.js:3717/:3757), not
    // from the user's select. The rated result is authoritative here — a
    // vendor may return a different row count / fin spacing than was asked
    // for. Null when unmatched, exactly as legacy's helper returns.
    fpi_id: getParamIdByNumberValue(fpisList, rated.fpi),
    rows_id: getParamIdByNumberValue(rowsList, rated.rows),
    header_mtl_id: s.header_mtl_id,
    inlet_header_dia: rated.inlet_header_dia,
    // Selector-parity — Manifold CONNECTION diameters: the SAVED id is derived
    // from the vendor's returned diameter, mapped back through the
    // connection_diameters list (coil.selector.js:3713/:3735), NOT the user's
    // select. Null when unmatched / list absent (niche: mfg 143/79 only).
    inlet_conn_dia_id: getParamIdByNumberValue(connectionDiameters, rated.inlet_conn_dia),
    mfg_id: s.mfg_id,
    model: rated.model,
    mounting_holes: s.mounting_holes,
    nitrogen_charge: s.nitrogen_charge,
    moisture_eliminator: s.moisture_eliminator,
    thermostatic_vent: s.thermostatic_vent,
    vacuum_breaker: s.vacuum_breaker,
    // The rating API's TOP-LEVEL notes, not the per-row key: legacy declares
    // `notes` twice in this literal and the SECOND (:3743, `self.NNotes` ⇒
    // `incoming_data.notes`) wins. The coercion loop below joins the array
    // with '\n' — 2779 of the 3148 legacy-written rows on dev3 hold a
    // multi-line string, which is what the print sheet and the Notes output
    // tab read back.
    notes: apiNotes ?? rated.notes ?? null,
    feeds: rated.feeds,
    feeds_id: rated.feeds_id ?? s.feeds_id ?? null,
    rae_feeds_id: s.rae_feeds_id ?? null,
    outlet_conn_dia_id: getParamIdByNumberValue(connectionDiameters, rated.outlet_conn_dia),
    outlet_header_dia: rated.outlet_header_dia,
    overall_length: rated.overall_length,
    // ★ Kept for literal parity with legacy :3742 but INERT on both stacks:
    // AHUCoilRatingMode has no `warnings` column and the serializer no such
    // field, so DRF drops it. Same for `rae_feeds_id` above. Do not "fix"
    // these by inventing columns — dev discards them too.
    warnings: rated.warnings,
    rating_datetime: s.rating_datetime,
    style_id: s.style_id,
    tube_cleanable_id: s.tube_cleanable_id,
    tube_dia_id: s.tube_dia_id,
    tube_interior_id: s.tube_interior_id,
    tube_mtl_id: s.tube_mtl_id,
    tube_wall_thick_id: s.tube_wall_thick_id,
    type_id: s.type_id,
    vent_drain_id: s.vent_drain_id,
    shipping_weight: numOrNull(rated.shipping_weight),
    coil_params: JSON.stringify(params),
    cost_registry_key: rated.cost_registry_key,
    // RPI — Decimal(5,2); legacy coil.selector.js 3355-3357 PATCHes rated_coil.rpi
    // (null-safe) onto rating_mode_coils/<id>.
    rpi: numOrNull(rated.rpi),
    circuiting_id: hasCircuitings ? numOrNull(s.circuiting_id) : null,
    circuiting_rating_output: rated.circuiting_rating_output,
    tube_hor_spacing: s.tube_hor_spacing,
    tube_hor_spacing_id: s.tube_hor_spacing_id,
    tube_vert_spacing: s.tube_vert_spacing,
    tube_vert_spacing_id: s.tube_vert_spacing_id,
    connection_per_header: s.connection_per_header,
  };
  // numeric coercion (legacy 3487): everything except the string/bool/JSON keys
  // and the distributor_* fields; notes/warnings arrays join('\n').
  const ratedCoilSave: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(ratedRaw)) {
    if (STRING_OR_BOOL_KEYS.has(k) || k.indexOf('distributor_') === 0) {
      ratedCoilSave[k] = v;
    } else if (v != null) {
      if (k === 'notes' || k === 'warnings') {
        ratedCoilSave[k] = Array.isArray(v) ? v.join('\n') : v;
      } else {
        const n = Number(String(v).replace(/,/g, ''));
        ratedCoilSave[k] = Number.isNaN(n) ? v : n;
      }
    } else {
      ratedCoilSave[k] = null;
    }
  }

  return { sectionSave, projectSave, currentModelSet, currentModelPatch, ratedCoilSave };
}

// ---------------------------------------------------------------------------
// Sync K — reheat position detection (dev 9ab54f1b01: coil.selector.js
// _heatingCoilHasUpstreamCooling + _tunnelOrderKey/_airflowSortKey, mirroring
// psychrometric_views.py's _tunnel_order_key/_section_airflow_sort_key). PURE:
// the selector assembles the inputs from the already-cached board RTKQ data —
// no network calls (dev derives the same set from stateStorage).
// ---------------------------------------------------------------------------

/** Minimal structural slices of IAhuBoardSection / IAhuCoilDetail /
 * IAhuBoardTunnel / ICmsTunnelIndex — kept structural so this stays a pure
 * data function over whatever cache rows the caller holds. */
export interface ReheatScanSection {
  id?: number | null;
  tunnel_id?: number | null;
  order_rate?: number | null;
  air_stream_id?: number | null;
  [key: string]: unknown;
}
export interface ReheatScanCoil {
  id?: number | null;
  section_id?: number | null;
  duty_id?: number | null;
  [key: string]: unknown;
}
export interface ReheatScanTunnel {
  id: number;
  index_id?: number | null;
  [key: string]: unknown;
}
export interface ReheatScanTunnelIndex {
  id: number;
  value?: string | number | null;
  [key: string]: unknown;
}

/**
 * Physical sequence key for a tunnel. Mirrors psychrometric_views.py's
 * _tunnel_order_key: [0, ...index row/col] when the tunnel has an index_id
 * whose CMS row (`cms.tunnels.indexes`, value like '2_1' — the same source
 * legacy tunnel.get_index_values() splits) parses cleanly; [1, tunnel_id]
 * otherwise (index-less/legacy tunnels, or index values unavailable — the
 * backend's except-branch and the legacy fallback are the same key).
 */
function tunnelOrderKey(
  tunnelId: number | null | undefined,
  tunnels: ReheatScanTunnel[],
  tunnelIndexes: ReheatScanTunnelIndex[]
): number[] {
  const tid = Number(tunnelId) || 0;
  const tunnel = tunnels.find((t) => Number(t.id) === tid);
  if (tunnel && tunnel.index_id != null) {
    const row = tunnelIndexes.find((i) => Number(i.id) === Number(tunnel.index_id));
    const values = String(row?.value ?? '')
      .split('_')
      .map((v) => parseInt(v, 10));
    if (values.length > 0 && values.every((v) => Number.isFinite(v))) {
      return [0, ...values];
    }
  }
  return [1, tid];
}

/** Tunnel order then order_rate ascending — ascending order_rate IS airflow
 * order (see _section_airflow_sort_key's docstring: the tunnel's
 * airflow_direction is 2D-schematic mirroring, never applied here). */
function airflowSortKey(
  section: ReheatScanSection,
  tunnels: ReheatScanTunnel[],
  tunnelIndexes: ReheatScanTunnelIndex[]
): number[] {
  return [
    ...tunnelOrderKey(section.tunnel_id, tunnels, tunnelIndexes),
    Number(section.order_rate) || 0,
  ];
}

function compareAirflowKeys(a: number[], b: number[]): number {
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i += 1) {
    const av = a[i] ?? 0;
    const bv = b[i] ?? 0;
    if (av !== bv) return av < bv ? -1 : 1;
  }
  return 0;
}

/**
 * True when at least one cooling-duty coil (duty_id 1) on the SAME
 * air_stream_id as `section` sits EARLIER in airflow order — i.e. a heating
 * coil in this section is in "reheat" position. ANY upstream cooling coil
 * counts (not just the nearest — same rule as _heating_coil_states in
 * psychrometric_views.py); coils on a different or null air stream never
 * factor in (they don't share the airflow path); a null own air_stream_id
 * → false. `coils` = every coil component on the unit; `sections` = every
 * section row (coil.section_id joins them — dev reads the live m.section
 * relation off stateStorage instead).
 */
export function heatingCoilHasUpstreamCooling(args: {
  section: ReheatScanSection | null | undefined;
  coils: ReheatScanCoil[];
  sections: ReheatScanSection[];
  tunnels: ReheatScanTunnel[];
  tunnelIndexes: ReheatScanTunnelIndex[];
}): boolean {
  const { section, coils, sections, tunnels, tunnelIndexes } = args;
  const airStreamId = section?.air_stream_id;
  if (section == null || airStreamId == null) return false;
  const sectionId = section.id;
  const ownKey = airflowSortKey(section, tunnels, tunnelIndexes);
  return coils.some((coil) => {
    if (Number(coil.duty_id) !== 1) return false; // cooling duty only
    const other = sections.find((row) => Number(row.id) === Number(coil.section_id));
    if (!other || Number(other.id) === Number(sectionId)) return false;
    if (other.air_stream_id == null || Number(other.air_stream_id) !== Number(airStreamId)) {
      return false;
    }
    return compareAirflowKeys(airflowSortKey(other, tunnels, tunnelIndexes), ownKey) < 0;
  });
}
